import TroisBarres from "../components/charte/TroisBarres";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, FormControl, FormControlLabel,
  Grid, IconButton, MenuItem, Select, Slider, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loaderOff, loaderOn, openSnackbar } from "../redux/actions";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import locationApi from "../services/locationApi";
import internalApi from "../utilities/internalApi";
import { getNiveaux } from "../utilities/locationService";
import { REGLES_VICTOIRE, libelleRegleVictoire } from "../utilities/regleVictoire";
import { modeleTuilesLeaflet, sousDomainesLeaflet, attributionFond } from "../utilities/basemap";
import { Produit, getProduits } from "../utilities/produitService";
import { ReponseComptage, compterZone } from "../utilities/comptageService";
import { Breakdown, simuler, declarerValeurRetenue } from "../utilities/pricingService";
import BoutonAide from "../components/charte/BoutonAide";
import { AIDE_CREER_QUETE } from "../aide/textesAide";

// Adresse de location-service, la même que celle du client unique (Z2-38) :
// relative en développement (proxy), celle du service en production.
const URL_LOCATION = locationApi.defaults.baseURL ?? "";

/* ═══════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════ */
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg:           "var(--c-bg)",
  card:         "var(--c-surface)",
  input:        "var(--c-surface)",
  border:       "var(--c-border)",
  borderInput:  "var(--c-border)",
  borderHover:  "#1F1E1B",
  accent:       "#1F1E1B",
  accentSoft:   "#57534A",
  accentBg:     "rgba(31,30,27,0.07)",
  accentBorder: "rgba(31,30,27,0.45)",
  text:         "#1F1E1B",
  sub:          "#57534A",
  label:        "#57534A",
  muted:        "#57534A",
  success:      "#356A0F",
  successBg:    "rgba(53,106,15,0.10)",
  successBdr:   "rgba(53,106,15,0.45)",
  gold:         "#7C5600",
  goldBg:       "rgba(124,86,0,0.10)",
  goldBdr:      "rgba(124,86,0,0.45)",
  silver:       "#57534A",
  silverBg:     "rgba(87,83,74,0.08)",
  silverBdr:    "rgba(87,83,74,0.35)",
  error:        "#A23A0D",
  previewBg:    "var(--c-surface-2)",
};

/**
 * Normalisation employée par la recherche du sélecteur de produits.
 *
 * MUI ignore déjà les accents nativement, mais par décomposition NFD, ce qui
 * ne suffit pas au référentiel : « Œufs » ne contient pas un O accentué, il
 * contient la ligature U+0152, que NFD ne décompose pas. Taper « oeufs » ne
 * trouvait donc rien. On étend la normalisation aux deux ligatures du
 * français, et on l'applique aux DEUX côtés de la comparaison pour que la
 * recherche marche aussi bien en tapant « œufs » qu'en tapant « oeufs ».
 */
const normaliserRecherche = (valeur: string) =>
  valeur
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/œ/gi, "oe").replace(/æ/gi, "ae")
    .toLowerCase().trim();

/* Habillage sombre de la liste déroulante du sélecteur de produits : l'appli
   n'a pas de thème MUI, le Paper serait blanc sur un formulaire sombre. */
const produitsPopupSx = {
  background: "var(--c-surface)", border: `1px solid ${C.borderInput}`,
  borderRadius: "8px", color: C.text, boxShadow: "none",
  "& .MuiAutocomplete-groupLabel": {
    background: "var(--c-surface)", color: C.muted, fontSize: "10px", fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.08em", lineHeight: "26px", top: 0,
  },
  "& .MuiAutocomplete-option": {
    fontSize: "13px", minHeight: "32px",
    '&[aria-selected="true"]': { background: C.accentBg, color: C.accentSoft },
    "&.Mui-focused": { background: "rgba(31,30,27,0.06)" },
    '&[aria-selected="true"].Mui-focused': { background: "rgba(31,30,27,0.18)" },
  },
  "& .MuiAutocomplete-noOptions": { color: C.sub, fontSize: "13px" },
};

/* ─── Shared dark field sx (no floating MUI label) ── */
const F: object = {
  "& .MuiOutlinedInput-root": {
    background: C.input, color: C.text, borderRadius: "8px", fontSize: "13px",
    "& fieldset": { borderColor: C.borderInput },
    "&:hover fieldset": { borderColor: C.borderHover },
    "&.Mui-focused fieldset": { borderColor: C.accent, borderWidth: "1px" },
  },
  "& .MuiInputBase-input": { color: C.text, padding: "10px 12px" },
  "& .MuiInputBase-input::placeholder": { color: C.muted, opacity: 1 },
  "& .MuiSelect-icon": { color: C.sub },
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiFormHelperText-root": { color: C.error, marginLeft: 0, fontSize: "11px" },
};

const menuPaper = {
  PaperProps: {
    sx: {
      background: "var(--c-surface)", border: `1px solid ${C.borderInput}`,
      borderRadius: "8px", color: C.text, boxShadow: "none",
      "& .MuiMenuItem-root": {
        fontSize: "13px", py: "8px",
        "&:hover": { background: C.accentBg },
        "&.Mui-selected": { background: C.accentBg, color: C.accentSoft },
        "&.Mui-selected:hover": { background: "rgba(31,30,27,0.18)" },
      },
    },
  },
};

/* ═══════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════ */
const QUEST_TYPES = [
  { id: "poi",            emoji: "📍", label: "Collecte de lieux"     },
  { id: "prix",           emoji: "🏷",  label: "Relevé de prix"       },
  { id: "photo",          emoji: "📸", label: "Photo terrain"         },
  { id: "enquete",        emoji: "📋", label: "Enquête"               },
  { id: "verification",   emoji: "✅", label: "Vérification de lieux" },
  { id: "infrastructure", emoji: "🏗",  label: "Infrastructure"        },
  { id: "signal",         emoji: "📶", label: "Signal réseau"         },
  { id: "sante",          emoji: "🏥", label: "Données sanitaires"    },
  { id: "agricole",       emoji: "🌾", label: "Données agricoles"     },
  { id: "commerce",       emoji: "🏪", label: "Recensement commerce"  },
];

// Catégories POI (categoryMain, taxo v2) pour typesLieuCibles (F1).
// Source canonique = endpoint GET /admin/references/type-lieu (décision Q1).
// La liste ci-dessous ne sert que de FALLBACK si l'endpoint est injoignable
// (ex. dev local sans admin-service up) — jamais de free-text (Q3b).
/* Le repli codé en dur a été retiré au lot 4.4. Il était l'une des trois
   copies de la même liste — avec la List.of de ReferenceController et la
   taxonomie du binaire Flutter — et les trois ignoraient « Autre ». Une
   seconde source de vérité diverge du jour où l'admin modifie le référentiel
   depuis /types_lieu, et elle le fait en silence. Même choix qu'en F3 : si le
   chargement échoue, le sélecteur reste vide et le dit. */
// Affichage adaptatif (Q3c) : ≤ seuil → tout affiché ; > seuil → HEAD + "Voir plus".
const POI_CATEGORIES_THRESHOLD = 15;
const POI_CATEGORIES_HEAD = 8;

const FIELD_TYPES    = ["texte","prix","choix","photo","gps","nombre"];
/* Repli si le référentiel des niveaux (GET /niveaux) est illisible. */
const NIVEAUX        = ["1","2","3","4","5"];
const ANCIENNETES    = ["1 mois","3 mois","6 mois","1 an"];

/* ── Cohorte : libellé UI ↔ contrat plat de QuestEntity ──────────────────
   Le service compare cohorteAnciennete à une ancienneté exprimée en JOURS
   (QuestController, ChronoUnit.DAYS) et cohorteStatut à la colonne
   user_master.account_status, dont la valeur est "active".
   null = aucune restriction : la plus petite option de chaque liste est la
   valeur par défaut du formulaire, jamais un choix explicite — on ne l'écrit
   donc pas, sous peine d'activer un filtre que personne n'a demandé. */
const ANCIENNETE_JOURS: Record<string, number> = {
  "1 mois": 30, "3 mois": 90, "6 mois": 180, "1 an": 365,
};
const ancienneteToJours = (label: string): number | null =>
  label === "1 mois" ? null : (ANCIENNETE_JOURS[label] ?? null);
const joursToAnciennete = (jours: unknown): string => {
  if (typeof jours !== "number" || !Number.isFinite(jours)) return "1 mois";
  return Object.keys(ANCIENNETE_JOURS).reduce(
    (best, label) =>
      Math.abs(ANCIENNETE_JOURS[label] - jours) < Math.abs(ANCIENNETE_JOURS[best] - jours)
        ? label : best,
    "1 mois",
  );
};
const statutToAccountStatus = (label: string): string | null =>
  label === "Actifs uniquement" ? "active" : null;   /* "Tous" → aucun filtre */
const accountStatusToStatut = (v: unknown): string =>
  v === "active" ? "Actifs uniquement" : "Tous";
const FREQUENCES     = ["Une fois","Quotidienne","Hebdomadaire","Mensuelle"];
/* Le commanditaire est saisi librement : la liste en dur proposait des
   institutions réelles (UNICEF, Banque Mondiale) sans lien avec Tribe. */

/* ═══════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════ */
interface BonusTier { topN: string; multiplier: string; }
interface DynField  {
  id: number; label: string; type: string; obligatoire: boolean; optionsJson: string;
  /* F3 — identifiant du produit dont ce champ a été généré. Présent tant que
     l'admin n'a pas modifié le libellé : dès qu'il le touche, la marque est
     retirée et le champ ne sera plus jamais réécrit ni supprimé
     automatiquement. Sa saisie prime. */
  genereDepuisProduit?: string;
}
interface FormState {
  titre: string; description: string; emailClient: string; commanditaire: string; logoPreviewUrl: string;
  types: string[];
  typesLieuCibles: string[];  /* F1 : catégories POI éligibles ; [] = aucune restriction */
  produitsCibles: string[];   /* F3 : identifiants du référentiel produits ; [] = aucun */
  latMin: string; latMax: string; lngMin: string; lngMax: string;
  frequence: string; dateDebut: string; dateFin: string;
  collectesAttendues: string; /* cible affichée sur la carte de la quête dans l'app */
  tokensParCollecte: string;  /* rétrocompatibilité : tokensBase chargé depuis l'API (ancien format) */
  budgetClient: string;       /* nouveau : budget en F CFA saisi par l'admin */
  margeTribe: number;         /* nouveau : % conservé par Tribe, 10-50, défaut 30 */
  bonusTiers: BonusTier[];
  niveauMin: string; ancienneteMin: string; statutCollecteur: string;
  collectesMin: string;
  notifPush: boolean; notifInApp: boolean; notifMessage: string;
}

/* ═══════════════════════════════════════════════════
   LEAFLET (dépendances npm, Z5-23)
═══════════════════════════════════════════════════ */
// Le module pose window.L et charge leaflet-draw ; la promesse est gardée
// pour ne pas changer les appelants.
function loadLeaflet(): Promise<void> {
  return import("../utilities/leafletDraw").then(() => undefined);
}

/* Zone définie ? — prédicat unique partagé par le hook de couverture,
   l'affichage du CoverageWidget et la validation de handleSave. */
function isZoneReady(
  zoneMode: "rectangle" | "polygon",
  form: Pick<FormState, "latMin" | "latMax" | "lngMin" | "lngMax">,
  zonePolygon: [number, number][],
): boolean {
  return zoneMode === "rectangle"
    ? !!(form.latMin && form.latMax && form.lngMin && form.lngMax)
    : zonePolygon.length >= 3;
}

/* ── Coverage score hook ── */
/* useCoverageScore et CoverageWidget ont été retirés au lot 5 du moteur.
   Ils chargeaient les 16 509 POI dans le navigateur à chaque changement de
   zone, puis calculaient une densité contre une constante DAKAR_DENSITY
   écrite en dur, d'où sortaient un délai, une marge et un volume suggéré.
   C'était du pricing, en parallèle du moteur et sans le dire. Les trois
   chiffres viennent désormais du serveur : le volume du comptage de zone, la
   marge et le prix du moteur. */

/* ═══════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════ */
function Label({ children, error, aide }: { children: React.ReactNode; error?: string; aide?: string }) {
  return (
    <Box sx={{ mb: "4px", display: "flex", alignItems: "center", gap: "6px" }}>
      <Typography sx={{
        fontSize: "11px", fontWeight: 600, letterSpacing: "0.09em",
        textTransform: "uppercase", color: error ? C.error : C.label, lineHeight: 1,
      }}>
        {children}
      </Typography>
      {aide && <BoutonAide texte={aide} />}
      {error && <Typography sx={{ fontSize: "11px", color: C.error }}>{error}</Typography>}
    </Box>
  );
}

/**
 * Ce que le client a demandé, quand le serveur n'a pas su le traduire tout seul.
 *
 * Un seul bandeau pour la zone et pour les produits : les deux disent la même
 * chose — voici la demande brute, à vous de la reprendre à la main — et deux
 * bandeaux différents pour le même propos se seraient mis à diverger.
 */
function BandeauDemandeClient({ icone, titre, texte }: { icone: string; titre: string; texte: string }) {
  return (
    <Box sx={{
      background: "rgba(124,86,0,0.08)", border: "1px solid rgba(124,86,0,0.25)",
      borderRadius: "8px", px: 2, py: "10px", mb: 2,
      display: "flex", alignItems: "flex-start", gap: 1.5,
    }}>
      <Typography sx={{ fontSize: "16px", lineHeight: 1, mt: "1px", flexShrink: 0 }}>{icone}</Typography>
      <Box>
        <Typography sx={{ fontSize: "12px", color: "#7C5600", fontWeight: 700, mb: "2px" }}>
          {titre}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: C.sub, lineHeight: 1.6 }}>
          {texte}
        </Typography>
      </Box>
    </Box>
  );
}

function SectionHeader({ n, children, aide }: { n: number; children: React.ReactNode; aide?: string }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", gap: 1.25,
      borderBottom: "1px solid var(--c-border)",
      pb: 1.25, mb: 2.5,
    }}>
      <TroisBarres taille="s" />
      <Typography component="h2" sx={{ fontFamily: "var(--font-display)", fontSize: "15px", fontWeight: 700, color: C.text }}>
        {n}. {children}
      </Typography>
      {aide && <BoutonAide texte={aide} />}
    </Box>
  );
}

function Card({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Box sx={{ background: C.card, borderRadius: "8px", border: "1.5px solid #1F1E1B", p: 2.5, ...sx }}>
      {children}
    </Box>
  );
}

function ToggleRow({ label, icon, checked, onChange, sub, aide }: {
  label: string; icon?: React.ReactNode; checked: boolean; onChange: (v: boolean) => void; sub?: string; aide?: string;
}) {
  return (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: C.input, borderRadius: "8px", border: `1px solid ${C.borderInput}`,
      px: 1.5, py: 1,
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon && <Box sx={{ color: C.label, display: "flex", alignItems: "center" }}>{icon}</Box>}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontSize: "13px", color: C.text, fontWeight: 500 }}>{label}</Typography>
            {aide && <BoutonAide texte={aide} />}
          </Box>
          {sub && <Typography sx={{ fontSize: "11px", color: C.muted, mt: "1px" }}>{sub}</Typography>}
        </Box>
      </Box>
      <Switch checked={checked} onChange={e => onChange(e.target.checked)} size="small" sx={{
        flexShrink: 0, ml: 1,
        "& .MuiSwitch-switchBase.Mui-checked": { color: C.accentSoft },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: C.accent },
        "& .MuiSwitch-track": { backgroundColor: "rgba(31,30,27,0.12)" },
      }} />
    </Box>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════ */
export default function CreateQuest() {

  const [form, setForm] = useState<FormState>({
    titre: "", description: "", emailClient: "", commanditaire: "", logoPreviewUrl: "",
    types: [],
    typesLieuCibles: [],
    produitsCibles: [],
    latMin: "", latMax: "", lngMin: "", lngMax: "",
    frequence: "Une fois", dateDebut: "", dateFin: "",
    collectesAttendues: "",
    tokensParCollecte: "",
    budgetClient: "",
    margeTribe: 30,
    bonusTiers: [{ topN: "10", multiplier: "2" }, { topN: "50", multiplier: "1.5" }],
    niveauMin: "1", ancienneteMin: "1 mois", statutCollecteur: "Tous",
    collectesMin: "0",
    notifPush: true, notifInApp: true, notifMessage: "",
  });

  const [fields,      setFields]      = useState<DynField[]>([]);
  const [errors,      setErrors]      = useState<Record<string, string>>({});
  const [dragOver,    setDragOver]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  /* Nom de zone demandé par le client, conservé par location-service quand la
     résolution Nominatim échoue. Lecture seule : l'admin trace, il ne le
     modifie pas. Renseigné uniquement sur les quêtes issues du portail IA. */
  const [zoneDemandee, setZoneDemandee] = useState<string>("");
  const [produitsDemandes, setProduitsDemandes] = useState<string[]>([]);

  /* Ce que Tribe a déjà référencé dans la zone tracée. Recalculé côté serveur
     à chaque changement de zone ou de types ciblés, avec temporisation : sans
     elle, déplacer un sommet de polygone déclencherait un appel par pixel. */
  const [comptage, setComptage] = useState<ReponseComptage | null>(null);
  const [comptageEnCours, setComptageEnCours] = useState(false);

  /* Suggestion du moteur de pricing. L'admin garde toujours la main : la
     suggestion pré-remplit, elle n'impose rien, et l'écart entre ce que le
     moteur proposait et ce qui est retenu reste visible à l'écran. */
  const [estimation, setEstimation] = useState<Breakdown | null>(null);
  const [estimationErreur, setEstimationErreur] = useState(false);
  /* Vrai dès que l'admin a touché un montant : on cesse alors de pré-remplir,
     sinon la frappe serait écrasée au prochain calcul. */
  const [montantsTouches, setMontantsTouches] = useState(false);
  /* Le prix d'une mission est une décision de Tribe (D3) : pré-rempli par le
     moteur, il doit être confirmé à la main avant l'enregistrement. Toute
     modification d'un montant annule la confirmation. */
  const [prixConfirme, setPrixConfirme] = useState(false);
  /* Niveaux proposés au ciblage : référentiel serveur (D7), libellés compris. */
  const [niveauxOptions, setNiveauxOptions] = useState<{ value: string; label: string }[]>(
    NIVEAUX.map(n => ({ value: n, label: `Niveau ${n}` })));
  useEffect(() => {
    let vivant = true;
    getNiveaux()
      .then(l => {
        if (vivant && l.length) setNiveauxOptions(l.map(n => ({
          value: String(n.niveau),
          label: `Niveau ${n.niveau}${n.nom ? ` — ${n.nom}` : ""}${n.actif ? "" : " (inactif)"}`,
        })));
      })
      .catch(() => { /* le repli reste affiché */ });
    return () => { vivant = false; };
  }, []);

  /* ── Challenge Mode ── */
  const [showAllLieux,            setShowAllLieux]            = useState(false); // F1 : déplier "Voir plus"
  const [poiCategories,           setPoiCategories]           = useState<string[]>([]);    // F1 : référentiel typesLieuCibles, chargé depuis admin-service
  const [poiErreur,               setPoiErreur]               = useState(false);
  /* Valeur d'un Tip en F CFA, lue sur la configuration active. Jamais en dur :
     c'est le pont entre les deux unités, et il n'existait que côté Flutter. */
  const [valeurDuTipCfa, setValeurDuTipCfa] = useState<number | null>(null);
  /* F3 — référentiel produits. Pas de repli codé en dur ici, contrairement à
     F1 : 66 produits recopiés dans le React seraient une seconde source de
     vérité qui divergerait du jour où l'admin modifie le référentiel. Si le
     chargement échoue, le sélecteur reste vide et le dit. */
  const [produits,      setProduits]      = useState<Produit[]>([]);
  const [produitsErreur, setProduitsErreur] = useState(false);
  const [challengeMode,           setChallengeMode]           = useState(false);
  /* Récompense du gagnant, en Tips (décision D12) : le challenge est interne,
     plus de lot physique ni de livraison à organiser. */
  const [challengeRecompenseTips, setChallengeRecompenseTips] = useState("");
  const [challengeRegleVictoire,  setChallengeRegleVictoire]  = useState("");
  /* Refus du serveur à l'enregistrement (budget gelé après devis), affiché sous le budget. */
  const [refusServeur, setRefusServeur] = useState<string | null>(null);
  const [challengeXeme,           setChallengeXeme]           = useState("");
  const [challengePushViral,      setChallengePushViral]      = useState(true);
  const [challengePushMessage,    setChallengePushMessage]    = useState("");
  const [missionRecurrente,       setMissionRecurrente]       = useState(false);
  const [frequenceCycle,          setFrequenceCycle]          = useState('hebdomadaire');
  const [dateFinContrat,          setDateFinContrat]          = useState('');
  const [validationAuto,          setValidationAuto]          = useState(false);
  const [analysePhotoAuto,        setAnalysePhotoAuto]        = useState(false);

  const [zoneMode,    setZoneMode]    = useState<"rectangle" | "polygon">("rectangle");
  const [zonePolygon, setZonePolygon] = useState<[number, number][]>([]);
  const [iaContext, setIaContext] = useState<{
    prompt: string;
    resumeIa?: string;
    valeurAjoutee?: string;
    reponses?: Array<{ question: string; reponse: string[] }>;
    clientEmail?: string;
  } | null>(null);

  const navigate     = useNavigate();
  const dispatch     = useDispatch();
  const [searchParams] = useSearchParams();
  const editId         = searchParams.get("id"); // null = création, string = édition

  const nextId       = useRef(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notifEdited  = useRef(false);
  const prevLogoUrl  = useRef<string>("");

  /* ── Leaflet ── */
  const mapContainerRef  = useRef<HTMLDivElement>(null);
  const leafletRef       = useRef<any>(null);
  const drawControlRef   = useRef<any>(null);
  const zoneModeRef      = useRef<"rectangle" | "polygon">("rectangle");
  const drawnLayersRef   = useRef<any>(null);
  const [mapReady, setMapReady] = useState(false);
  /* true le temps d'une restauration de zone en édition : empêche l'effet
     [zoneMode] d'effacer la zone qu'on vient juste de recharger. */
  const hydratingZoneRef = useRef(false);
  /* la zone chargée n'est redessinée qu'une fois sur la carte */
  const zoneRenderedRef  = useRef(false);

  useEffect(() => {
    let mounted = true;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const initMap = () => {
      if (!mounted || !mapContainerRef.current || leafletRef.current) return;

      const L = (window as any).L;

      // Retry if L or L.Draw not yet available (loadLeaflet resolved early via
      // waitFor but a race with React render can still beat the poll interval)
      if (!L || !L.Draw) {
        retryTimer = setTimeout(initMap, 100);
        return;
      }

      // "Map container is already initialized" — vérification native Leaflet
      if ((mapContainerRef.current as any)._leaflet_id) return;

      const map = L.map(mapContainerRef.current).setView([14.69, -17.44], 7);
      L.tileLayer(modeleTuilesLeaflet(true), {
        subdomains: sousDomainesLeaflet(),
        attribution: attributionFond,
      }).addTo(map);

      const drawn = new L.FeatureGroup();
      map.addLayer(drawn);
      drawnLayersRef.current = drawn;

      const buildDrawOpts = (mode: "rectangle" | "polygon") => mode === "rectangle"
        // showArea désactivé : leaflet-draw 1.0.4 lève « type is not defined »
        // en calculant la surface quand il est empaqueté (code strict).
        ? { rectangle: { showArea: false, shapeOptions: { color: C.accent, weight: 2, fillOpacity: 0.08 } }, polygon: false, circle: false, marker: false, circlemarker: false, polyline: false }
        : { rectangle: false, polygon: { shapeOptions: { color: "#1F1E1B", weight: 2, fillOpacity: 0.2 } }, circle: false, marker: false, circlemarker: false, polyline: false };

      drawControlRef.current = new L.Control.Draw({
        edit: { featureGroup: drawn },
        draw: buildDrawOpts(zoneModeRef.current),
      });
      map.addControl(drawControlRef.current);

      const updateBounds = (layer: any) => {
        const b = layer.getBounds();
        setForm(p => ({
          ...p,
          latMin: b.getSouth().toFixed(5), latMax: b.getNorth().toFixed(5),
          lngMin: b.getWest().toFixed(5),  lngMax: b.getEast().toFixed(5),
        }));
      };

      const clearZoneError = () =>
        setErrors(p => { const n = { ...p }; delete n.zone; return n; });

      map.on(L.Draw.Event.CREATED, (e: any) => {
        drawn.clearLayers();
        drawn.addLayer(e.layer);
        clearZoneError();
        if (zoneModeRef.current === "rectangle") {
          updateBounds(e.layer);
        } else {
          const lls: [number, number][] = e.layer.getLatLngs()[0].map((ll: any) => [ll.lat, ll.lng] as [number, number]);
          setZonePolygon(lls);
        }
      });
      map.on(L.Draw.Event.EDITED, (e: any) => {
        clearZoneError();
        e.layers.eachLayer((l: any) => {
          if (zoneModeRef.current === "rectangle") updateBounds(l);
          else {
            const lls: [number, number][] = l.getLatLngs()[0].map((ll: any) => [ll.lat, ll.lng] as [number, number]);
            setZonePolygon(lls);
          }
        });
      });
      map.on(L.Draw.Event.DELETED, () => {
        if (zoneModeRef.current === "rectangle") setForm(p => ({ ...p, latMin: "", latMax: "", lngMin: "", lngMax: "" }));
        else setZonePolygon([]);
      });

      leafletRef.current = map;
      setMapReady(true);
    };

    loadLeaflet().then(initMap);

    return () => {
      mounted = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (leafletRef.current) {
        try {
          if (drawControlRef.current) leafletRef.current.removeControl(drawControlRef.current);
        } catch {}
        drawControlRef.current = null;
        leafletRef.current.remove();
        leafletRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => { if (prevLogoUrl.current) URL.revokeObjectURL(prevLogoUrl.current); };
  }, []);

  /* Valeur du Tip — /config/active est public, aucun jeton requis. */
  useEffect(() => {
    let vivant = true;
    fetch("https://dev-admin.tribe.sn/config/active")
      .then(r => r.json())
      .then(c => { if (vivant && typeof c?.tipValueCfa === "number" && c.tipValueCfa > 0) setValeurDuTipCfa(c.tipValueCfa); })
      .catch(() => { /* le bloc de rémunération se met en attente, voir plus bas */ });
    return () => { vivant = false; };
  }, []);

  /* F1 — référentiel catégories POI, source unique : la table servie par
     admin-service. Aucun repli local, voir le commentaire en tête de fichier. */
  useEffect(() => {
    let alive = true;
    internalApi.get("/admin/references/type-lieu")
      .then(r => {
        const list = Array.isArray(r.data) ? r.data.filter((x: any): x is string => typeof x === "string") : [];
        if (alive) { setPoiCategories(list); setPoiErreur(list.length === 0); }
      })
      .catch(() => { if (alive) setPoiErreur(true); });
    return () => { alive = false; };
  }, []);

  /* Comptage de zone — source unique : le serveur. Temporisé, sinon déplacer
     un sommet de polygone déclencherait un appel par pixel parcouru. */
  useEffect(() => {
    if (!isZoneReady(zoneMode, form, zonePolygon)) { setComptage(null); return; }
    let vivant = true;
    const minuterie = setTimeout(() => {
      setComptageEnCours(true);
      compterZone({
        ...(zoneMode === "rectangle"
          ? { latMin: +form.latMin, latMax: +form.latMax, lngMin: +form.lngMin, lngMax: +form.lngMax }
          : { zonePolygon }),
        typesLieuCibles: form.typesLieuCibles.length ? form.typesLieuCibles : undefined,
      })
        .then(r => { if (vivant) setComptage(r); })
        .catch(() => { if (vivant) setComptage(null); })
        .finally(() => { if (vivant) setComptageEnCours(false); });
    }, 600);
    return () => { vivant = false; clearTimeout(minuterie); };
  }, [zoneMode, form.latMin, form.latMax, form.lngMin, form.lngMax, zonePolygon, form.typesLieuCibles]);

  /* Le moteur de pricing, sur demande explicite de l'admin.

     Chaque appel à /pricing/estimate écrit une ligne pricing_estimate_log,
     et l'API n'a pas d'option pour ne pas journaliser. L'appel automatique
     (temporisé à chaque frappe) gonflait les métriques de calibration
     d'estimations jamais retenues : on n'estime plus qu'au clic.

     SI LE MOTEUR NE RÉPOND PAS, RIEN NE BLOQUE : les montants restent
     saisissables, un message le dit, et l'enregistrement fonctionne. */
  const descripteurPricing = useMemo(() => {
    if (!form.typesLieuCibles.length) return null;
    const zoneOk = isZoneReady(zoneMode, form, zonePolygon);
    return {
      canal: "backoffice",
      /* Toutes les catégories cochées : le moteur retient la plus chère et
         l'applique à tous les lieux (Z5-29). categoryMain reste pour un
         moteur antérieur, qui ne lit que lui. */
      categoryMain: form.typesLieuCibles[0],
      categories: form.typesLieuCibles,
      champsCollecte: fields.map(f => f.type),
      collectesAttendues: parseInt(form.collectesAttendues) || 1,
      margeOverride: form.margeTribe > 0 && form.margeTribe < 100
        ? form.margeTribe / 100 : undefined,
      ...(zoneOk
        ? (zoneMode === "rectangle"
            ? { latMin: +form.latMin, latMax: +form.latMax, lngMin: +form.lngMin, lngMax: +form.lngMax }
            : { zonePolygon })
        : {}),
    };
  }, [form.typesLieuCibles, form.collectesAttendues, form.margeTribe,
      form.latMin, form.latMax, form.lngMin, form.lngMax, zoneMode, zonePolygon, fields]);
  const clePricing = descripteurPricing ? JSON.stringify(descripteurPricing) : null;
  const [cleEstimee, setCleEstimee] = useState<string | null>(null);
  const [estimationEnCours, setEstimationEnCours] = useState(false);

  const lancerEstimation = async () => {
    if (!descripteurPricing) return;
    setEstimationEnCours(true);
    try {
      const b = await simuler(descripteurPricing);
      setEstimation(b); setEstimationErreur(false); setCleEstimee(clePricing);
    } catch {
      setEstimation(null); setEstimationErreur(true);
    } finally { setEstimationEnCours(false); }
  };

  /* Pré-remplissage : tant que l'admin n'a touché à rien, les montants
     suivent la suggestion. Dès qu'il en modifie un, on ne l'écrase plus. */
  useEffect(() => {
    if (!estimation || montantsTouches) return;
    setForm(p => ({
      ...p,
      tokensParCollecte: String(estimation.recompenseSpotterTips),
      budgetClient: String(estimation.coutClientFcfa),
      bonusTiers: estimation.bonusVitesse.length
        ? estimation.bonusVitesse.map(v => ({ topN: String(v.rang), multiplier: String(v.multiplicateur) }))
        : p.bonusTiers,
    }));
  }, [estimation, montantsTouches]);

  useEffect(() => { setPrixConfirme(false); }, [form.tokensParCollecte, form.budgetClient]);

  /* F3 — référentiel produits (source canonique = admin-service). */
  useEffect(() => {
    let alive = true;
    getProduits(true)
      .then(liste => { if (alive) { setProduits(liste); setProduitsErreur(false); } })
      .catch(() => { if (alive) setProduitsErreur(true); });
    return () => { alive = false; };
  }, []);

  /* ── Switch mode zone (rectangle ↔ polygone) ── */
  useEffect(() => {
    zoneModeRef.current = zoneMode;
    /* consommé même si la carte n'est pas encore prête : dans ce cas rien
       n'est purgé non plus, la zone restaurée survit. */
    const hydrating = hydratingZoneRef.current;
    hydratingZoneRef.current = false;
    const L = (window as any).L;
    const map = leafletRef.current;
    const drawn = drawnLayersRef.current;
    if (!L?.Control?.Draw || !map || !drawn) return;
    if (drawControlRef.current) {
      try { map.removeControl(drawControlRef.current); } catch {}
      drawControlRef.current = null;
    }
    if (!hydrating) {
      drawn.clearLayers();
      setForm(p => ({ ...p, latMin: "", latMax: "", lngMin: "", lngMax: "" }));
      setZonePolygon([]);
    }
    const drawOpts = zoneMode === "rectangle"
      ? { rectangle: { showArea: false, shapeOptions: { color: C.accent, weight: 2, fillOpacity: 0.08 } }, polygon: false, circle: false, marker: false, circlemarker: false, polyline: false }
      : { rectangle: false, polygon: { shapeOptions: { color: "#1F1E1B", weight: 2, fillOpacity: 0.2 } }, circle: false, marker: false, circlemarker: false, polyline: false };
    drawControlRef.current = new L.Control.Draw({ edit: { featureGroup: drawn }, draw: drawOpts });
    map.addControl(drawControlRef.current);
  }, [zoneMode]);

  /* ── Chargement en mode édition ── */
  useEffect(() => {
    if (!editId) return;
    const FREQ_REVERSE: Record<string, string> = {
      une_fois: "Une fois", quotidienne: "Quotidienne",
      hebdomadaire: "Hebdomadaire", mensuelle: "Mensuelle",
    };
    const isoToDate = (s?: string) => s ? s.slice(0, 10) : "";

    setLoadingEdit(true);
    fetch(`${URL_LOCATION}/quest/${editId}`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((q: any) => {
        /* Zone : GET /quest/{id} renvoie QuestEntity à plat. Le mode est déduit
           du contenu, pas d'un champ stocké. */
        const polygone: [number, number][] = Array.isArray(q.zonePolygon)
          ? q.zonePolygon
              .filter((pt: any) => Array.isArray(pt) && pt.length >= 2)
              .map((pt: any) => [Number(pt[0]), Number(pt[1])] as [number, number])
              .filter((pt: [number, number]) => pt.every(Number.isFinite))
          : [];
        const modeZone = polygone.length >= 3 ? "polygon" : "rectangle";
        /* n'armer la garde que si l'effet [zoneMode] va réellement se déclencher,
           sinon le drapeau resterait à true et neutraliserait la purge lors du
           prochain basculement manuel de mode. */
        setZoneDemandee(typeof q.zoneDemandee === "string" ? q.zoneDemandee.trim() : "");
          setProduitsDemandes(Array.isArray(q.produitsDemandes)
            ? q.produitsDemandes.map((v: any) => String(v)).filter(Boolean) : []);
        hydratingZoneRef.current = modeZone !== zoneModeRef.current;
        setZoneMode(modeZone);
        setZonePolygon(polygone);
        setForm({
          titre:              q.titre              ?? "",
          description:        q.description        ?? "",
          emailClient:        "",
          commanditaire:      q.commanditaire      ?? "",
          logoPreviewUrl:     q.logoUrl            ?? "",
          types:              q.types              ?? [],
          typesLieuCibles:    q.typesLieuCibles    ?? [],   /* rétrocompat : absent sur vieilles quêtes → [] */
          produitsCibles:     Array.isArray(q.produitsCibles)
                                ? q.produitsCibles.map((v: any) => String(v))
                                : [],
          /* L'API sérialise QuestEntity à plat : il n'y a pas d'enveloppe `zone`.
             q.zone?.latMin valait donc toujours undefined et la zone n'était
             jamais rechargée à l'ouverture d'une quête en édition. */
          latMin:             q.latMin != null ? String(q.latMin) : "",
          latMax:             q.latMax != null ? String(q.latMax) : "",
          lngMin:             q.lngMin != null ? String(q.lngMin) : "",
          lngMax:             q.lngMax != null ? String(q.lngMax) : "",
          frequence:          FREQ_REVERSE[q.frequence] ?? "Une fois",
          dateDebut:          isoToDate(q.dateDebut),
          dateFin:            isoToDate(q.dateFin),
          collectesAttendues: q.collectesAttendues != null ? String(q.collectesAttendues) : "",
          tokensParCollecte:  q.tokensBase        != null ? String(q.tokensBase)          : "",
          budgetClient:       q.budgetClient      != null ? String(q.budgetClient)         : "",
          margeTribe:         q.margeTribe        != null ? Number(q.margeTribe)           : 30,
          /* L'API ne renvoie pas de tableau `bonusVitesse` : les deux paliers
             sont quatre champs à plat. L'écriture était déjà correcte, seule
             cette relecture était fausse — les paliers enregistrés
             repartaient donc systématiquement sur les valeurs par défaut. */
          bonusTiers: [
            { topN:       q.bonusN1Collecteurs    != null ? String(q.bonusN1Collecteurs)    : "10",
              multiplier: q.bonusN1Multiplicateur != null ? String(q.bonusN1Multiplicateur) : "2" },
            { topN:       q.bonusN2Collecteurs    != null ? String(q.bonusN2Collecteurs)    : "50",
              multiplier: q.bonusN2Multiplicateur != null ? String(q.bonusN2Multiplicateur) : "1.5" },
          ],
          niveauMin:          q.cohorteNiveauMin    != null ? String(q.cohorteNiveauMin)    : "1",
          ancienneteMin:      joursToAnciennete(q.cohorteAnciennete),
          statutCollecteur:   accountStatusToStatut(q.cohorteStatut),
          collectesMin:       q.cohorteCollectesMin != null ? String(q.cohorteCollectesMin) : "0",
          notifPush:          q.notifPush    ?? true,
          notifInApp:         q.notifInApp   ?? true,
          notifMessage:       q.notifMessage ?? "",
        });
        /* Réglages relus tels qu'enregistrés : laissés à leur valeur par
           défaut, ils repartaient à false à chaque mise à jour (validation
           automatique, analyse photo, récurrence coupées en silence). */
        setValidationAuto(q.validationAuto === true);
        setAnalysePhotoAuto(q.analysePhotoAuto === true);
        setMissionRecurrente(q.cycleActif === true);
        if (q.frequenceCycle === "hebdomadaire" || q.frequenceCycle === "mensuelle") setFrequenceCycle(q.frequenceCycle);
        setDateFinContrat(isoToDate(q.dateFinContrat));
        /* Une quête enregistrée a déjà un prix retenu : la suggestion du
           moteur ne doit pas l'écraser en silence à la réouverture. */
        if (Number(q.tokensBase) > 0) setMontantsTouches(true);
        if (q.challengeMode) {
          setChallengeMode(true);
          setChallengeRecompenseTips(q.challengeRecompenseTips != null ? String(q.challengeRecompenseTips) : "");
          setChallengeRegleVictoire(q.challengeRegleVictoire ?? "");
          setChallengePushViral(q.challengePushViral ?? true);
        }
        if (q.champs?.length) {
          setFields(q.champs.map((f: any, idx: number) => ({
            id: idx + 1,
            label:       f.label       ?? "",
            type:        f.type        ?? "texte",
            obligatoire: f.obligatoire ?? false,
            optionsJson: Array.isArray(f.options) ? f.options.join(", ") : (f.optionsJson ?? ""),
            /* Sans relire la marque, un champ généré redeviendrait « manuel »
               à chaque réouverture et ne se nettoierait plus quand l'admin
               retire le produit correspondant. */
            genereDepuisProduit: typeof f.genereDepuisProduit === "string" ? f.genereDepuisProduit : undefined,
          })));
          nextId.current = q.champs.length + 1;
        }
        if (q.source === "client_ia") {
          fetch(`https://dev-admin.tribe.sn/mission/ia-request/by-quest/${editId}`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}` },
          })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data) return;
              let reponses: Array<{ question: string; reponse: string[] }> = [];
              try { reponses = data.reponsesJson ? JSON.parse(data.reponsesJson) : []; } catch {}
              setIaContext({
                prompt: data.prompt ?? "",
                resumeIa: data.resumeIa ?? data.description ?? "",
                valeurAjoutee: data.valeurAjoutee ?? "",
                reponses,
                clientEmail: data.clientEmail ?? "",
              });
            })
            .catch(() => {});
        }
      })
      .catch(err => dispatch(openSnackbar(`Impossible de charger la quête : ${err.message}`, "red")))
      .finally(() => setLoadingEdit(false));
  }, [editId]);

  /* ── Édition : redessiner la zone chargée sur la carte (une seule fois) ── */
  useEffect(() => {
    if (!editId || !mapReady || zoneRenderedRef.current) return;
    const L = (window as any).L;
    const map = leafletRef.current;
    const drawn = drawnLayersRef.current;
    if (!L || !map || !drawn) return;

    let layer: any = null;
    if (zoneMode === "polygon" && zonePolygon.length >= 3) {
      layer = L.polygon(zonePolygon, { color: "#1F1E1B", weight: 2, fillOpacity: 0.2 });
    } else if (form.latMin && form.latMax && form.lngMin && form.lngMax) {
      layer = L.rectangle(
        [[+form.latMin, +form.lngMin], [+form.latMax, +form.lngMax]],
        { color: C.accent, weight: 2, fillOpacity: 0.08 },
      );
    }
    if (!layer) return;

    drawn.clearLayers();
    drawn.addLayer(layer);
    try { map.fitBounds(layer.getBounds(), { padding: [20, 20] }); } catch {}
    zoneRenderedRef.current = true;
  }, [editId, mapReady, zoneMode, zonePolygon,
      form.latMin, form.latMax, form.lngMin, form.lngMax]);

  /* ── Helpers ── */
  const set = useCallback((k: keyof FormState, v: any) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => { const e = { ...p }; delete e[k]; return e; });
  }, []);

  const handleTitreChange = (v: string) => {
    setForm(p => ({ ...p, titre: v, notifMessage: notifEdited.current ? p.notifMessage : v }));
    setErrors(p => { const e = { ...p }; delete e.titre; return e; });
  };

  const toggleType = (id: string) =>
    set("types", form.types.includes(id)
      ? form.types.filter(t => t !== id)
      : [...form.types, id]);

  /**
   * F3 — un champ « prix » par produit ciblé.
   *
   * Un libellé qui énumérerait plusieurs produits demanderait un seul chiffre
   * pour trois produits : la donnée serait inexploitable, et l'export ne
   * pourrait pas donner une colonne par produit.
   *
   * Les champs générés portent genereDepuisProduit et se réécrivent quand la
   * sélection change. Dès que l'admin modifie un libellé, la marque tombe :
   * le champ devient le sien et n'est plus touché.
   */
  const synchroniserChampsProduits = useCallback((idsCibles: string[]) => {
    setFields(precedents => {
      const parProduit = new Map(produits.map(p => [String(p.id), p.libelle]));

      // On retire les champs générés dont le produit n'est plus ciblé.
      const conserves = precedents.filter(f =>
        !f.genereDepuisProduit || idsCibles.includes(f.genereDepuisProduit));

      const dejaGeneres = new Set(
        conserves.map(f => f.genereDepuisProduit).filter(Boolean) as string[]);

      const ajouts = idsCibles
        .filter(id => !dejaGeneres.has(id) && parProduit.has(id))
        .map(id => ({
          id: nextId.current++,
          label: `Prix du ${(parProduit.get(id) ?? "").toLowerCase()}`,
          type: "prix",
          obligatoire: true,
          optionsJson: "",
          genereDepuisProduit: id,
        }));

      return [...conserves, ...ajouts];
    });
  }, [produits]);

  /**
   * Produits demandés par le client que le serveur n'a pas su résoudre.
   *
   * Le rapprochement reprend la normalisation du serveur — espaces réduits,
   * casse ignorée — de sorte qu'un terme apparaisse ici si et seulement s'il
   * n'a pas donné de produit ciblé à la création. On n'affiche pas les termes
   * résolus : ils sont déjà cochés, les répéter n'appellerait aucune action.
   */
  const produitsNonResolus = useMemo(() => {
    if (produitsDemandes.length === 0) return [];
    const cle = (v: string) => v.trim().replace(/\s+/g, " ").toLowerCase();
    const cibles = new Set(
      produits.filter(p => form.produitsCibles.includes(String(p.id))).map(p => cle(p.libelle)));
    return produitsDemandes.filter(terme => !cibles.has(cle(terme)));
  }, [produitsDemandes, produits, form.produitsCibles]);

  /* F3 — Produits ciblés (multi-select pastilles, groupées par catégorie) */
  /* Options du sélecteur, ordonnées comme le référentiel : groupBy de MUI
     regroupe des options CONSÉCUTIVES, il faut donc trier par ordre de
     catégorie avant, puis par libellé à l'intérieur de chaque catégorie. */
  const produitsTries = useMemo(() => {
    const collator = new Intl.Collator("fr");
    return [...produits].sort((a, b) => {
      const oa = a.categorie?.ordre ?? Number.MAX_SAFE_INTEGER;
      const ob = b.categorie?.ordre ?? Number.MAX_SAFE_INTEGER;
      if (oa !== ob) return oa - ob;
      const parNom = collator.compare(a.categorie?.libelle ?? "", b.categorie?.libelle ?? "");
      return parNom !== 0 ? parNom : collator.compare(a.libelle, b.libelle);
    });
  }, [produits]);

  const produitsSelectionnes = useMemo(
    () => form.produitsCibles
      .map(id => produitsTries.find(p => String(p.id) === id))
      .filter((p): p is Produit => !!p),
    [form.produitsCibles, produitsTries]);

  /* Identifiants ciblés qui ne correspondent à aucune option : produit
     désactivé depuis la création de la quête, ou référentiel pas encore
     chargé. Le sélecteur ne peut pas les afficher, mais il ne doit pas les
     perdre — le motif en ligne les préservait par construction, puisqu'il
     n'agissait que sur l'identifiant cliqué. On les remet donc dans la
     valeur à chaque changement. */
  const produitsCiblesInconnus = useMemo(
    () => form.produitsCibles.filter(id => !produitsTries.some(p => String(p.id) === id)),
    [form.produitsCibles, produitsTries]);

  const changerProduits = (valeurs: Produit[]) => {
    const suivants = [...valeurs.map(p => String(p.id)), ...produitsCiblesInconnus];
    set("produitsCibles", suivants);
    synchroniserChampsProduits(suivants);
  };

  /* F1 — Types de lieu ciblés (multi-select pastilles) */
  const toggleLieu = (cat: string) =>
    set("typesLieuCibles", form.typesLieuCibles.includes(cat)
      ? form.typesLieuCibles.filter(c => c !== cat)
      : [...form.typesLieuCibles, cat]);

  const setBonusTier = (idx: number, k: keyof BonusTier, v: string) =>
    setForm(p => ({ ...p, bonusTiers: p.bonusTiers.map((t, i) => i === idx ? { ...t, [k]: v } : t) }));

  const handleLogoFile = (file: File | null) => {
    if (!file || !file.type.startsWith("image/")) return;
    if (prevLogoUrl.current) URL.revokeObjectURL(prevLogoUrl.current);
    const url = URL.createObjectURL(file);
    prevLogoUrl.current = url;
    set("logoPreviewUrl", url);
  };

  const addField    = () => setFields(p => [...p, { id: nextId.current++, label: "", type: "texte", obligatoire: false, optionsJson: "" }]);
  const removeField = (id: number) => setFields(p => p.filter(f => f.id !== id));
  const updateField = (id: number, k: keyof DynField, v: any) => setFields(p => p.map(f => {
    if (f.id !== id) return f;
    // Modifier le libellé d'un champ généré le rend à l'admin : il perd sa
    // marque et ne sera plus jamais réécrit ni supprimé automatiquement.
    const rendu = k === "label" && f.genereDepuisProduit ? { genereDepuisProduit: undefined } : {};
    return { ...f, [k]: v, ...rendu };
  }));
  const moveField   = (idx: number, dir: -1 | 1) => {
    setFields(p => {
      const a = [...p]; const sw = idx + dir;
      if (sw < 0 || sw >= a.length) return p;
      [a[idx], a[sw]] = [a[sw], a[idx]];
      return a;
    });
  };

  const clearZone = () => {
    if (drawnLayersRef.current) drawnLayersRef.current.clearLayers();
    setZonePolygon([]);
  };

  /* ── Duration ── */
  const calcDuration = (): string | null => {
    if (!form.dateDebut || !form.dateFin) return null;
    const d = Math.round((new Date(form.dateFin).getTime() - new Date(form.dateDebut).getTime()) / 86_400_000);
    if (d < 0) return null;
    if (d === 0) return "1 jour";
    if (d < 14)  return `${d} jour${d > 1 ? "s" : ""}`;
    if (d < 60)  return `${Math.round(d / 7)} semaine${Math.round(d / 7) > 1 ? "s" : ""}`;
    return `${Math.round(d / 30)} mois`;
  };

  /* ── Jours restants (= ce que Flutter calcule depuis dateFin) ── */
  const calcJoursRestants = (): number | null => {
    if (!form.dateFin) return null;
    const d = Math.round((new Date(form.dateFin).getTime() - Date.now()) / 86_400_000);
    return d >= 0 ? d : 0;
  };

  /* ── Budget & rémunération — calculs automatiques ── */
  const budgetNum      = parseFloat(form.budgetClient.replace(/\s/g, "")) || 0;
  const collectesNum   = parseInt(form.collectesAttendues) || 0;
  const tribePart      = Math.round(budgetNum * (form.margeTribe / 100));
  const budgetCollecteurs = budgetNum - tribePart;
  /* Ces deux lignes ne servent plus qu'à AFFICHER la décomposition d'un budget
     saisi. Elles ne produisent plus tokensBase : le sens du calcul s'est
     inversé au lot 5 — on part de l'effort demandé au Spotter et le prix
     client en découle par la marge, et non l'inverse. */
  /* Ce qui part au serveur : la valeur saisie (pré-remplie par le moteur)
     et confirmée. Jamais de repli silencieux sur la suggestion ni sur 0 :
     le serveur refuse tokensBase <= 0 (D3). */
  const tipsSaisis = Math.round(parseFloat(form.tokensParCollecte)) || 0;
  const base  = tipsSaisis;

  /* ── Bonus preview ── */
  const t1    = form.bonusTiers[0];
  const t2    = form.bonusTiers[1];
  const top1  = parseInt(t1?.topN)         || 0;
  const top2  = parseInt(t2?.topN)         || 0;
  const mult1 = parseFloat(t1?.multiplier) || 1;
  const mult2 = parseFloat(t2?.multiplier) || 1;

  /* ── Save ── */
  const logoLocal = form.logoPreviewUrl.startsWith("blob:");

  const handleSave = async () => {
    const e: Record<string, string> = {};
    if (!form.titre.trim())               e.titre              = "Le titre est obligatoire";
    if (form.types.length === 0)          e.types              = "Sélectionnez au moins un type de collecte";
    if (!isZoneReady(zoneMode, form, zonePolygon))
      e.zone = zoneMode === "rectangle"
        ? "Délimitez la zone de collecte sur la carte (outil rectangle)"
        : "Tracez un polygone d'au moins 3 points sur la carte";
    if (!form.dateFin)                    e.dateFin            = "La date de fin est obligatoire";
    else if (form.dateDebut && form.dateFin < form.dateDebut)
      e.dateFin = "La date de fin doit être postérieure ou égale à la date de début";
    if (missionRecurrente && dateFinContrat && form.dateDebut && dateFinContrat < form.dateDebut)
      e.dateFinContrat = "La fin du contrat doit être postérieure ou égale à la date de début";
    if (!(budgetNum > 0))                 e.budgetClient       = "Définissez le budget client";
    if (!(tipsSaisis > 0))                e.tokensParCollecte  = "Les Tips par collecte doivent être supérieurs à 0";
    if (challengeMode && challengeRecompenseTips !== ""
        && !(Number.isInteger(Number(challengeRecompenseTips)) && Number(challengeRecompenseTips) >= 0))
      e.challengeRecompenseTips = "La récompense est un nombre entier de Tips, 0 ou plus";
    if (!prixConfirme)                    e.prixConfirme       = "Confirmez le prix avant d'enregistrer";
    if (form.types.includes("prix") && form.produitsCibles.length === 0)
      e.produitsCibles = "Une quête de relevé de prix doit cibler au moins un produit — "
                       + "sans quoi le Spotter relève un prix sans savoir de quoi.";
    if (fields.length === 0)             e.champs             = "Ajoutez au moins un champ de collecte";
    if (!(+form.collectesAttendues > 0)) e.collectesAttendues = "Définissez le nombre de collectes attendues";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    /* ── helpers ── */
    /* Dakar est à UTC toute l'année. Une date de fin « YYYY-MM-DD » devenait
       minuit UTC, soit le DÉBUT du jour choisi : la quête se fermait un jour
       trop tôt. La fin couvre désormais toute la journée. */
    const debutDuJour = (d: string) => d ? `${d}T00:00:00.000Z` : null;
    const finDuJour   = (d: string) => d ? `${d}T23:59:59.999Z` : null;
    const toInt = (v: string | number) => Math.round(Number(v)) || 0;
    const FREQ_MAP: Record<string, string> = {
      "Une fois": "une_fois", "Quotidienne": "quotidienne",
      "Hebdomadaire": "hebdomadaire", "Mensuelle": "mensuelle",
    };

    const payload = {
      titre:              form.titre.trim(),
      description:        form.description,
      ...(form.emailClient.trim() ? { createdBy: form.emailClient.trim() } : {}),
      commanditaire:      form.commanditaire.trim(),
      /* Une URL blob: n'existe que dans cet onglet : aucun téléversement
         n'est branché, on ne l'enregistre pas (le serveur la refuse aussi).
         En édition, l'absence du champ conserve le logo déjà enregistré. */
      ...(logoLocal ? {} : { logoUrl: form.logoPreviewUrl || null }),
      types:              form.types,                                          // string[]
      /* Listes envoyées telles quelles, même vides : null était ignoré par
         le PUT, et une restriction ne pouvait plus être retirée en édition.
         Vide = aucune restriction. */
      typesLieuCibles:    form.typesLieuCibles,                                // F1
      produitsCibles:     form.produitsCibles,                                 // F3 : identifiants, jamais les libellés
      champs: fields.map(({ id, ...f }, idx) => ({
        ...f, ordre: idx + 1,
        options: f.type === "choix"
          ? f.optionsJson.split(",").map(s => s.trim()).filter(Boolean)
          : undefined,
      })),
      ...(zoneMode === "rectangle" && form.latMin
        ? { latMin: +form.latMin, latMax: +form.latMax, lngMin: +form.lngMin, lngMax: +form.lngMax }
        : {}),
      ...(zoneMode === "polygon" && zonePolygon.length >= 3 ? { zonePolygon } : {}),
      frequence:          FREQ_MAP[form.frequence] ?? "une_fois",              // snake_case Java
      dateDebut:          debutDuJour(form.dateDebut) ?? new Date().toISOString(),   // défaut aujourd'hui
      dateFin:            finDuJour(form.dateFin),                                   // ISO 8601, fin de journée
      /* duree et joursRestants ne sont plus envoyés : dérivés de dateDebut
         et dateFin, lus par aucun consommateur, et joursRestants serait faux
         dès le lendemain. Ils restent calculés pour l'affichage local. */
      collectesAttendues: toInt(form.collectesAttendues),                      // integer
      collectesRealisees: 0,                                                   // défaut création
      budgetClient:       budgetNum || null,
      /* Ce que le moteur proposait, conservé à côté de ce qui est retenu.
         C'est l'écart entre les deux qui recalibrera la grille. */
      tokensBaseSuggere:      estimation?.recompenseSpotterTips ?? null,
      budgetClientSuggere:    estimation?.coutClientFcfa ?? null,
      pricingRulesetVersion:  estimation?.rulesetVersion ?? null,
      pricingEstimateLogId:   estimation?.estimateLogId ?? null,
      margeTribe:         form.margeTribe,
      tokensBase:         tipsSaisis,                                          // valeur retenue et confirmée, en Tips — jamais 0 (refusé plus haut)
      bonusN1Collecteurs:     toInt(form.bonusTiers[0]?.topN) || null,
      bonusN1Multiplicateur:  parseFloat(form.bonusTiers[0]?.multiplier) || null,
      bonusN2Collecteurs:     toInt(form.bonusTiers[1]?.topN) || null,
      bonusN2Multiplicateur:  parseFloat(form.bonusTiers[1]?.multiplier) || null,
      /* Cohorte — contrat plat de QuestEntity. L'objet `cohorte` imbriqué
         envoyé jusqu'ici était silencieusement ignoré par Jackson
         (FAIL_ON_UNKNOWN_PROPERTIES=false par défaut) : rien n'était persisté.
         null = aucune restriction, pour ne pas activer le filtre de
         GET /quest/active sur les valeurs par défaut du formulaire. */
      cohorteNiveauMin:    toInt(form.niveauMin) > 1 ? toInt(form.niveauMin) : null,
      cohorteAnciennete:   ancienneteToJours(form.ancienneteMin),
      cohorteStatut:       statutToAccountStatus(form.statutCollecteur),
      cohorteCollectesMin: toInt(form.collectesMin) > 0 ? toInt(form.collectesMin) : null,
      /* Pas de statut : le serveur crée en « à valider », et une mise à jour
         ne change jamais le statut (il ne change que par Publier, Dépublier
         ou Clôturer). Envoyer « brouillon » faisait repasser en brouillon une
         quête client à valider. */
      /* Notifications — contrat plat. Même cause que la cohorte : l'objet
         `notifications` imbriqué n'arrivait jamais en base, notifPush restait
         null et la publication poussait donc toujours un push. */
      notifPush:    form.notifPush,
      notifInApp:   form.notifInApp,
      notifMessage: form.notifMessage,
      /* Toujours envoyé : absent, la mise à jour ne le change pas, et
         décocher le Challenge en édition ne le désactiverait pas. */
      challengeMode,
      ...(challengeMode ? {
        challengeRecompenseTips: challengeRecompenseTips !== "" ? Math.round(Number(challengeRecompenseTips)) : 0,
        challengeRegleVictoire: challengeRegleVictoire === "Xème collecteur" && challengeXeme
          ? `Xème collecteur (${challengeXeme})`
          : challengeRegleVictoire,
        challengePushViral,
      } : {}),
      validationAuto,
      analysePhotoAuto,
      parentQuestId: null,
      frequenceCycle: missionRecurrente ? frequenceCycle : null,
      cycleActif: missionRecurrente,
      /* Toujours envoyée : null efface la fin de contrat d'une mission
         repassée en unique. Le scheduler ne crée plus de cycle après elle. */
      dateFinContrat: missionRecurrente ? finDuJour(dateFinContrat) : null,
    };

    const isEditing = !!editId;
    const QUEST_URL = isEditing
      ? `${URL_LOCATION}/quest/${editId}`
      : `${URL_LOCATION}/quest`;
    const method  = isEditing ? "PUT" : "POST";
    const bodyStr = JSON.stringify(payload);

    setSaving(true);
    dispatch(loaderOn());
    try {
      const token = sessionStorage.getItem("jwtToken");
      const res = await fetch(QUEST_URL, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: bodyStr,
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => "");
        console.error(`[CreateQuest] HTTP ${res.status}:`, errBody);
        // Budget gelé après un devis (409) ou facturation injoignable (503) :
        // le message du serveur, tel quel.
        if ((res.status === 409 || res.status === 503) && errBody.trim()) {
          setRefusServeur(errBody.trim());
          throw Object.assign(new Error(errBody.trim()), { verbatim: true });
        }
        throw new Error(`HTTP ${res.status}${errBody ? ` — ${errBody}` : ""}`);
      }
      setRefusServeur(null);
      dispatch(openSnackbar(isEditing ? "Quête mise à jour." : "Quête enregistrée avec succès.", "green"));

      /* Déclarer au moteur ce qui a réellement été retenu. Sans effet sur la
         quête : c'est de l'instrumentation, et declarerValeurRetenue avale
         ses propres erreurs — perdre un point de calibration ne doit jamais
         gêner l'enregistrement d'une mission. */
      if (estimation?.estimateLogId != null) {
        const enregistree = await res.clone().json().catch(() => null);
        declarerValeurRetenue(estimation.estimateLogId, base, enregistree?.id ?? editId ?? undefined);
      }

      /* Aucun push ici : la quête n'est pas publiée à l'enregistrement.
         Le push « Challenge » partait vers tous les Spotters à chaque
         enregistrement, brouillon compris ; seule la publication notifie. */

      navigate("/quetes/liste");
    } catch (err: any) {
      dispatch(openSnackbar(err?.verbatim ? err.message : `Erreur lors de l'enregistrement : ${err.message}`, "red"));
    } finally {
      setSaving(false);
      dispatch(loaderOff());
    }
  };

  const duration       = calcDuration();
  const joursRestants  = calcJoursRestants();

  /* ════════════════════════════════════════ RENDER */
  return (
    <Box sx={{ background: C.bg, minHeight: "100vh", color: C.text, p: { xs: 2, md: 3 } }}>

      {/* ── Page header ── */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 3 }}>
        <Box>
          {loadingEdit ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={16} sx={{ color: C.accentSoft }} />
              <Typography sx={{ fontSize: "13px", color: C.sub }}>Chargement de la quête…</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Typography sx={{ fontSize: "22px", fontWeight: 800, letterSpacing: "-0.02em", color: C.text, lineHeight: 1.2 }}>
                  {editId ? "Modifier la quête" : "Créer une quête"}
                </Typography>
                <BoutonAide texte={AIDE_CREER_QUETE.page} />
              </Box>
              <Typography sx={{ fontSize: "13px", color: C.sub, mt: 0.5 }}>
                {editId ? "Modifiez les paramètres puis enregistrez." : "Configurez tous les paramètres avant publication."}
              </Typography>
            </>
          )}
        </Box>
        <Button
          startIcon={saving
            ? <CircularProgress size={14} sx={{ color: "#fff" }} />
            : <SaveOutlinedIcon sx={{ fontSize: "15px !important" }} />}
          onClick={handleSave}
          disabled={saving || loadingEdit || !prixConfirme}
          sx={{
            background: (saving || loadingEdit || !prixConfirme) ? "rgba(31,30,27,0.5)" : C.accent,
            color: "#fff", fontWeight: 700, fontSize: "13px",
            textTransform: "none", borderRadius: "8px", px: 2.5, py: "9px",
            ml: 2, flexShrink: 0, whiteSpace: "nowrap",
            boxShadow: "none",
            "&:hover": { background: (saving || loadingEdit) ? "rgba(31,30,27,0.5)" : "#1F1E1B", boxShadow: "none" },
            "&.Mui-disabled": { color: "#fff", opacity: 0.7 },
          }}
        >
          {saving ? "Enregistrement…" : editId ? "Mettre à jour" : "Enregistrer"}
        </Button>
      </Box>

      {iaContext && (
        <Box sx={{
          mb: 3, p: "20px",
          background: "rgba(31,30,27,0.08)",
          border: `1px solid ${C.accentBorder}`,
          borderRadius: "8px",
          boxShadow: "none",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Box component="span" sx={{ fontSize: 20 }}>✦</Box>
            <Typography sx={{ color: C.accentSoft, fontWeight: 600, fontSize: 14, letterSpacing: 0.3 }}>
              SOUMISSION CLIENT À VALIDER
            </Typography>
            {iaContext.clientEmail && (
              <Typography sx={{ ml: "auto", color: C.sub, fontSize: 13 }}>
                {iaContext.clientEmail}
              </Typography>
            )}
          </Box>

          <Typography sx={{ color: C.text, fontSize: 13, mb: 2.5, opacity: 0.85 }}>
            Complétez la zone géographique, le budget, les Tips et les dates avant de valider et publier cette quête.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: C.label, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, mb: 0.5 }}>
              PROMPT INITIAL
            </Typography>
            <Typography sx={{ color: C.text, fontSize: 14, fontStyle: "italic" }}>
              "{iaContext.prompt}"
            </Typography>
          </Box>

          {iaContext.reponses && iaContext.reponses.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: C.label, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, mb: 1 }}>
                PRÉCISIONS DEMANDÉES PAR L'IA
              </Typography>
              {iaContext.reponses.map((qa, i) => (
                <Box key={i} sx={{ mb: 1, pl: 1.5, borderLeft: `2px solid ${C.accentBorder}` }}>
                  <Typography sx={{ color: C.sub, fontSize: 12, mb: 0.3 }}>{qa.question}</Typography>
                  <Typography sx={{ color: C.text, fontSize: 13 }}>
                    → {Array.isArray(qa.reponse) ? qa.reponse.join(", ") : qa.reponse}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {iaContext.valeurAjoutee && (
            <Box>
              <Typography sx={{ color: C.label, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, mb: 0.5 }}>
                VALEUR AJOUTÉE PERÇUE
              </Typography>
              <Typography sx={{ color: C.text, fontSize: 13, opacity: 0.9 }}>
                {iaContext.valeurAjoutee}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Grid container spacing={2.5}>

        {/* ══════════════════════════════════════════
            SEC 1 — Informations générales (col gauche)
        ══════════════════════════════════════════ */}
        <Grid item xs={12} lg={6}>
          <Card>
            <SectionHeader n={1} aide={AIDE_CREER_QUETE.informations}>Informations générales</SectionHeader>
            <Grid container spacing={2}>

              <Grid item xs={12}>
                <Label error={errors.titre}>Titre *</Label>
                <TextField fullWidth value={form.titre}
                  onChange={e => handleTitreChange(e.target.value)}
                  placeholder="Ex : Recensement pharmacies Dakar"
                  error={!!errors.titre} helperText={errors.titre}
                  sx={F} />
              </Grid>

              <Grid item xs={12}>
                <Label>Description</Label>
                <TextField fullWidth multiline rows={3} value={form.description}
                  onChange={e => set("description", e.target.value)}
                  placeholder="Décrivez l'objectif de la quête (affiché sous le titre dans l'application)…"
                  sx={F} />
              </Grid>

              <Grid item xs={12}>
                <Label>Email client (optionnel)</Label>
                <TextField fullWidth value={form.emailClient}
                  onChange={e => set("emailClient", e.target.value)}
                  placeholder="client@organisation.sn"
                  sx={F} />
              </Grid>

              <Grid item xs={12}>
                <Label>Commanditaire</Label>
                <TextField fullWidth value={form.commanditaire}
                  onChange={e => set("commanditaire", e.target.value)}
                  placeholder="Nom de l'organisation commanditaire"
                  sx={F} />
              </Grid>

              {/* Logo upload / drop */}
              <Grid item xs={12}>
                <Label>Logo client</Label>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={e => handleLogoFile(e.target.files?.[0] ?? null)} />
                <Box
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleLogoFile(e.dataTransfer.files[0] ?? null); }}
                  sx={{
                    border: `1px dashed ${dragOver ? C.accentSoft : form.logoPreviewUrl ? C.accentBorder : C.borderInput}`,
                    borderRadius: "8px", background: dragOver ? C.accentBg : C.input,
                    display: "flex", alignItems: "center", gap: 2, p: "12px 16px",
                    cursor: "pointer", transition: "all 0.15s ease",
                    "&:hover": { borderColor: C.borderHover, background: "rgba(31,30,27,0.05)" },
                  }}
                >
                  {form.logoPreviewUrl ? (
                    <>
                      <Box component="img" src={form.logoPreviewUrl} alt="Logo"
                        sx={{ width: 48, height: 48, objectFit: "contain", borderRadius: "6px",
                          border: `1px solid ${C.border}`, background: "#fff", flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ fontSize: "13px", color: C.text, fontWeight: 500 }}>
                          {logoLocal ? "Aperçu local" : "Logo enregistré"}
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: logoLocal ? C.gold : C.sub }}>
                          {logoLocal
                            ? "Le téléversement n'est pas disponible : ce logo ne sera pas enregistré."
                            : "Cliquez pour remplacer"}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box sx={{
                        width: 40, height: 40, borderRadius: "8px", flexShrink: 0,
                        background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <ImageOutlinedIcon sx={{ fontSize: 20, color: C.accentSoft }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "13px", color: C.sub }}>
                          Glissez une image ici ou{" "}
                          <span style={{ color: C.accentSoft, fontWeight: 600 }}>cliquez pour parcourir</span>
                        </Typography>
                        <Typography sx={{ fontSize: "11px", color: C.muted, mt: "2px" }}>PNG, JPG, SVG</Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>

              {/* Types de collecte — badges cliquables */}
              <Grid item xs={12}>
                <Label error={errors.types} aide={AIDE_CREER_QUETE.typesCollecte}>Types de collecte *</Label>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: 0.5 }}>
                  {QUEST_TYPES.map(t => {
                    const on = form.types.includes(t.id);
                    return (
                      <Box key={t.id} onClick={() => toggleType(t.id)} sx={{
                        display: "inline-flex", alignItems: "center", gap: "5px",
                        px: "10px", py: "6px", borderRadius: "8px",
                        cursor: "pointer", userSelect: "none",
                        fontSize: "12px", fontWeight: on ? 600 : 400,
                        background:  on ? C.accentBg  : "rgba(31,30,27,0.04)",
                        color:       on ? C.accentSoft : C.sub,
                        border: `1px solid ${on ? C.accentBorder : "rgba(31,30,27,0.08)"}`,
                        transition: "all 0.12s ease",
                        "&:hover": {
                          background:  on ? "rgba(31,30,27,0.17)" : "rgba(31,30,27,0.07)",
                          borderColor: on ? C.accentBorder : "rgba(31,30,27,0.15)",
                        },
                      }}>
                        <span style={{ fontSize: "13px", lineHeight: 1 }}>{t.emoji}</span>
                        {t.label}
                      </Box>
                    );
                  })}
                </Box>
                {errors.types && (
                  <Typography sx={{ fontSize: "11px", color: C.error, mt: "4px" }}>{errors.types}</Typography>
                )}
              </Grid>

              {/* Types de lieu ciblés (F1) — restriction des soumissions par catégorie POI */}
              <Grid item xs={12}>
                <Label aide={AIDE_CREER_QUETE.typesLieu}>Types de lieu ciblés</Label>
                <Typography sx={{ fontSize: "11px", color: C.sub, mt: "-2px", mb: "6px" }}>
                  Le Spotter ne peut soumettre que sur ces types de lieux. Si vide, aucune restriction.
                </Typography>
                {poiErreur && (
                  <Typography sx={{ fontSize: "12px", color: C.error, mb: "6px" }}>
                    Référentiel des types de lieu injoignable. Rechargez la page — la
                    liste n'est plus recopiée dans le back-office, il n'y a pas de repli.
                  </Typography>
                )}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {(poiCategories.length > POI_CATEGORIES_THRESHOLD && !showAllLieux
                      ? poiCategories.slice(0, POI_CATEGORIES_HEAD)
                      : poiCategories).map(cat => {
                    const on = form.typesLieuCibles.includes(cat);
                    return (
                      <Box key={cat} onClick={() => toggleLieu(cat)} sx={{
                        display: "inline-flex", alignItems: "center",
                        px: "10px", py: "6px", borderRadius: "8px",
                        cursor: "pointer", userSelect: "none",
                        fontSize: "12px", fontWeight: on ? 600 : 400,
                        background:  on ? C.accentBg  : "rgba(31,30,27,0.04)",
                        color:       on ? C.accentSoft : C.sub,
                        border: `1px solid ${on ? C.accentBorder : "rgba(31,30,27,0.08)"}`,
                        transition: "all 0.12s ease",
                        "&:hover": {
                          background:  on ? "rgba(31,30,27,0.17)" : "rgba(31,30,27,0.07)",
                          borderColor: on ? C.accentBorder : "rgba(31,30,27,0.15)",
                        },
                      }}>
                        {cat}
                      </Box>
                    );
                  })}
                  {poiCategories.length > POI_CATEGORIES_THRESHOLD && (
                    <Box onClick={() => setShowAllLieux(v => !v)} sx={{
                      display: "inline-flex", alignItems: "center",
                      px: "10px", py: "6px", borderRadius: "8px",
                      cursor: "pointer", userSelect: "none",
                      fontSize: "12px", fontWeight: 500,
                      background: "transparent", color: C.accentSoft,
                      border: `1px dashed ${C.accentBorder}`,
                    }}>
                      {showAllLieux ? "Voir moins" : `Voir plus (+${poiCategories.length - POI_CATEGORIES_HEAD})`}
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Produits ciblés (F3) — référentiel fermé, jamais de texte libre */}
              <Grid item xs={12}>
                <Label aide={AIDE_CREER_QUETE.produits}>Produits ciblés</Label>
                <Typography sx={{ fontSize: "11px", color: C.sub, mt: "-2px", mb: "6px" }}>
                  Produits génériques, jamais des marques. Si vide, aucun produit n'est ciblé.
                </Typography>

                {produitsNonResolus.length > 0 && (
                  <BandeauDemandeClient
                    icone="🏷️"
                    titre={`Produit${produitsNonResolus.length > 1 ? "s" : ""} demandé${produitsNonResolus.length > 1 ? "s" : ""} par le client : « ${produitsNonResolus.join(" », « ")} »`}
                    texte="Le référentiel ne contient aucun produit sous ce nom. Ouvrez le sélecteur ci-dessous pour choisir l'équivalent, ou ajoutez-le au référentiel s'il y manque. Une quête de relevé de prix ne peut pas être publiée sans produit ciblé."
                  />
                )}
                {errors.produitsCibles && (
                  <Typography sx={{ fontSize: "11px", color: C.error, mb: "6px" }}>
                    {errors.produitsCibles}
                  </Typography>
                )}
                {produitsErreur ? (
                  <Typography sx={{ fontSize: "12px", color: C.error }}>
                    Référentiel produits injoignable. Rechargez la page — la liste
                    n'est pas recopiée dans le back-office, il n'y a pas de repli.
                  </Typography>
                ) : produits.length === 0 ? (
                  <Typography sx={{ fontSize: "12px", color: C.muted }}>
                    Aucun produit actif dans le référentiel.
                  </Typography>
                ) : (
                  /* Combobox plutôt que pastilles en ligne : le référentiel
                     compte déjà 66 produits sur dix catégories, et il est
                     éditable, donc il grandira. Le motif en ligne reste le bon
                     pour les onze types de lieu, qui sont stables. */
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    limitTags={6}
                    options={produitsTries}
                    value={produitsSelectionnes}
                    onChange={(_, valeurs) => changerProduits(valeurs)}
                    groupBy={p => p.categorie?.libelle ?? "Sans catégorie"}
                    getOptionLabel={p => p.libelle}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    filterOptions={(options, { inputValue }) => {
                      const q = normaliserRecherche(inputValue);
                      return q ? options.filter(p => normaliserRecherche(p.libelle).includes(q)) : options;
                    }}
                    noOptionsText="Aucun produit ne correspond"
                    slotProps={{ paper: { sx: produitsPopupSx } }}
                    sx={{
                      "& .MuiAutocomplete-popupIndicator": { color: C.sub },
                      "& .MuiAutocomplete-clearIndicator": { color: C.sub },
                    }}
                    renderOption={(props, p, { selected }) => (
                      <li {...props} key={p.id}>
                        <Checkbox
                          size="small" checked={selected}
                          sx={{ mr: "6px", p: "2px", color: C.sub, "&.Mui-checked": { color: C.accentSoft } }}
                        />
                        {p.libelle}
                      </li>
                    )}
                    renderTags={(valeurs, getTagProps) =>
                      valeurs.map((p, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={p.id}
                          label={p.libelle}
                          size="small"
                          sx={{
                            height: "24px", fontSize: "12px",
                            background: C.accentBg, color: C.accentSoft,
                            border: `1px solid ${C.accentBorder}`,
                            "& .MuiChip-deleteIcon": {
                              color: C.accentSoft, fontSize: "15px",
                              "&:hover": { color: C.text },
                            },
                          }}
                        />
                      ))
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={produitsSelectionnes.length === 0 ? "Rechercher un produit…" : ""}
                        sx={[F, { "& .MuiAutocomplete-input": { padding: "5px 4px !important" } }]}
                      />
                    )}
                  />
                )}
              </Grid>

              {/* ── Challenge Mode ── */}
              <Grid item xs={12}>
                <Box sx={{
                  background: challengeMode ? "rgba(31,30,27,0.08)" : C.input,
                  border: `1px solid ${challengeMode ? "rgba(31,30,27,0.2)" : C.borderInput}`,
                  borderRadius: "8px", p: "14px 16px",
                  transition: "all 0.18s ease",
                }}>
                  {/* Toggle row */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "16px", lineHeight: 1 }}>🏆</Typography>
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Typography sx={{ fontSize: "13px", color: C.text, fontWeight: 600 }}>
                            Activer le mode Challenge
                          </Typography>
                          <BoutonAide texte={AIDE_CREER_QUETE.challenge} />
                        </Box>
                        <Typography sx={{ fontSize: "11px", color: C.muted, mt: "1px" }}>
                          Le gagnant désigné reçoit une récompense en Tips
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={challengeMode}
                      onChange={e => {
                        setChallengeMode(e.target.checked);
                        if (e.target.checked) setChallengePushViral(true);
                      }}
                      size="small"
                      sx={{
                        flexShrink: 0, ml: 1,
                        "& .MuiSwitch-switchBase.Mui-checked": { color: C.accentSoft },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: C.accent },
                        "& .MuiSwitch-track": { backgroundColor: "rgba(31,30,27,0.12)" },
                      }}
                    />
                  </Box>

                  {challengeMode && (
                    <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>

                      {/* Récompense en Tips */}
                      <Box>
                        <Label error={errors.challengeRecompenseTips}>Récompense du gagnant (Tips)</Label>
                        <TextField fullWidth type="number" value={challengeRecompenseTips}
                          onChange={e => setChallengeRecompenseTips(e.target.value)}
                          placeholder="ex: 500"
                          inputProps={{ min: 0, step: 1 }}
                          error={!!errors.challengeRecompenseTips}
                          helperText={errors.challengeRecompenseTips}
                          sx={F} />
                        <Typography sx={{ fontSize: "11px", color: C.muted, mt: "4px" }}>
                          Crédités sur le solde du gagnant quand vous le désignez
                          {valeurDuTipCfa && Number(challengeRecompenseTips) > 0
                            ? ` (≈ ${Math.round(Number(challengeRecompenseTips) * valeurDuTipCfa).toLocaleString("fr-FR")} F CFA)`
                            : ""}.
                        </Typography>
                      </Box>

                      {/* Règle de victoire */}
                      <Box>
                        <Label aide={AIDE_CREER_QUETE.regleVictoire}>Règle de victoire</Label>
                        <FormControl fullWidth sx={F}>
                          <Select value={challengeRegleVictoire}
                            onChange={e => setChallengeRegleVictoire(e.target.value)}
                            displayEmpty MenuProps={menuPaper}
                            renderValue={v => (v ? libelleRegleVictoire(v) : <span style={{ color: C.muted }}>Sélectionner…</span>)}>
                            {REGLES_VICTOIRE.map(o =>
                              <MenuItem key={o} value={o}>{libelleRegleVictoire(o)}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Box>

                      {challengeRegleVictoire === "Xème collecteur" && (
                        <Box>
                          <Label>Numéro du Spotter gagnant (X)</Label>
                          <TextField fullWidth type="number" value={challengeXeme}
                            onChange={e => setChallengeXeme(e.target.value)}
                            placeholder="ex: 100"
                            inputProps={{ min: 1 }}
                            sx={F} />
                        </Box>
                      )}

                      {/* Push viral */}
                      <ToggleRow
                        label="Push viral au lancement"
                        aide={AIDE_CREER_QUETE.pushViral}
                        sub="Notifier les Spotters éligibles à la quête au moment de la publication"
                        icon={<NotificationsOutlinedIcon sx={{ fontSize: 16 }} />}
                        checked={challengePushViral}
                        onChange={setChallengePushViral}
                      />

                      {/* Message push personnalisé */}
                      <Box>
                        <Label>Message personnalisé pour le push (optionnel)</Label>
                        <TextField fullWidth multiline rows={2} value={challengePushMessage}
                          onChange={e => setChallengePushMessage(e.target.value)}
                          placeholder={`${form.titre || "Titre de la quête"} — ${challengeRecompenseTips || "N"} Tips à gagner !`}
                          sx={F} />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>

            </Grid>
          </Card>
        </Grid>

        {/* ══════════════════════════════════════════
            SEC 3 — Zone géographique (col droite)
        ══════════════════════════════════════════ */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: "100%", boxSizing: "border-box" }}>
            <SectionHeader n={3} aide={AIDE_CREER_QUETE.zone}>Zone géographique *</SectionHeader>

            {/* La zone demandée par le client n'apparaît que si elle n'a pas
                pu être résolue : quand la bbox existe, il n'y a rien à tracer
                et l'information n'a plus d'objet. */}
            {!!zoneDemandee && !isZoneReady(zoneMode, form, zonePolygon) && (
              <BandeauDemandeClient
                icone="📍"
                titre={`Zone demandée par le client : « ${zoneDemandee} »`}
                texte="La résolution automatique n'a rien trouvé pour ce nom dans la région de Dakar. Tracez la zone correspondante ci-dessous."
              />
            )}

            {/* Ce que Tribe a déjà référencé dans la zone. Chiffres du serveur,
                jamais recalculés ici : c'est la même source que l'instantané
                figé sur la quête. */}
            {comptage && comptage.comptage.total > 0 && (
              <Box sx={{
                background: "rgba(53,106,15,0.06)", border: "1px solid rgba(53,106,15,0.20)",
                borderRadius: "8px", px: 2, py: "12px", mb: 2,
              }}>
                <Typography sx={{ fontSize: "12px", color: C.success, fontWeight: 700, mb: "6px" }}>
                  Nous avons {comptage.comptage.total.toLocaleString("fr-FR")} lieux référencés dans cette zone
                  {comptageEnCours && " …"}
                </Typography>

                {/* Niveau 1 d'abord : c'est le seul qui soit contractuel, c'est
                    lui que typesLieuCibles filtrera à la soumission. */}
                {comptage.comptage.parCategorie.slice(0, 4).map(l => (
                  <Typography key={l.libelle} sx={{ fontSize: "11px", color: C.sub, lineHeight: 1.7 }}>
                    {l.libelle} : <strong style={{ color: C.text }}>{l.total}</strong>
                    <span style={{ color: C.muted }}>
                      {" "}— {l.osm} issus de données publiques, {l.tribe} vérifiés par nos équipes
                    </span>
                  </Typography>
                ))}

                {/* Niveau fin : descriptif, jamais contractuel. En dessous de
                    dix on ne cite pas un chiffre précis, il serait plus
                    trompeur qu'utile. */}
                {comptage.comptage.parSousType.filter(l => l.total >= 10).length > 0 && (
                  <Typography sx={{ fontSize: "11px", color: C.sub, mt: "6px", lineHeight: 1.7 }}>
                    dont{" "}
                    {comptage.comptage.parSousType.filter(l => l.total >= 10).slice(0, 5)
                      .map(l => `${l.total} ${l.libelle.toLowerCase()}`).join(", ")}
                  </Typography>
                )}

                <Typography sx={{ fontSize: "10px", color: C.muted, mt: "8px", lineHeight: 1.6 }}>
                  {comptage.comptage.sansNiveauFin.toLocaleString("fr-FR")} de ces lieux n'ont pas encore
                  de type précis. Les chiffres ci-dessus sont donc un plancher : une mission complète
                  et met à jour ce socle.
                </Typography>

                {/* Bloc interne, volontairement séparé et étiqueté. Ce qui est
                    constaté et ce qu'un modèle suppose ne doivent jamais se
                    lire d'un seul tenant. */}
                {comptage.estimationInterne?.attendu?.length > 0 && (
                  <Box sx={{
                    mt: 1.5, pt: 1.5, borderTop: `1px dashed ${C.borderInput}`,
                  }}>
                    <Typography sx={{ fontSize: "10px", color: C.muted, textTransform: "uppercase",
                      letterSpacing: "0.08em", fontWeight: 700, mb: "4px" }}>
                      Estimation interne · fiabilité {comptage.estimationInterne.fiabilite} % · ne pas communiquer
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: C.sub, lineHeight: 1.7 }}>
                      {comptage.estimationInterne.attendu.slice(0, 4)
                        .map(l => `${l.libelle} : ${l.connu} connues, ~${l.attendu} attendues`).join(" · ")}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: C.muted, mt: "6px", lineHeight: 1.6 }}>
                      {comptage.estimationInterne.avertissement}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Mode toggle */}
            <Box sx={{ display: "flex", gap: "8px", mb: 2 }}>
              {(["rectangle", "polygon"] as const).map((mode) => (
                <Box
                  key={mode}
                  component="button"
                  onClick={() => setZoneMode(mode)}
                  sx={{
                    flex: 1, py: "8px",
                    border: `1px solid ${zoneMode === mode ? C.accent : C.borderInput}`,
                    background: zoneMode === mode ? "#1F1E1B" : "var(--c-surface)",
                    borderRadius: "8px", cursor: "pointer",
                    color: zoneMode === mode ? "#fff" : C.sub,
                    fontSize: "13px", fontWeight: 600,
                    transition: "all 0.15s",
                  }}
                >
                  {mode === "rectangle" ? "▭ Rectangle" : "⬠ Polygone libre"}
                </Box>
              ))}
            </Box>

            {/* Instruction */}
            <Box sx={{
              background: C.accentBg, border: `1px solid ${C.accentBorder}`,
              borderRadius: "8px", px: 2, py: "10px", mb: 2,
              display: "flex", alignItems: "flex-start", gap: 1.5,
            }}>
              <Typography sx={{ fontSize: "16px", lineHeight: 1, mt: "1px", flexShrink: 0 }}>✏️</Typography>
              <Typography sx={{ fontSize: "12px", color: C.sub, lineHeight: 1.6 }}>
                <strong style={{ color: C.accentSoft }}>Délimitez la zone de collecte sur la carte</strong>
                {zoneMode === "rectangle"
                  ? <>{" "}— activez l'outil rectangle <strong style={{ color: C.text }}>▭</strong> dans la barre d'outils Leaflet, tracez puis redimensionnez la zone.</>
                  : <>{" "}— activez l'outil polygone dans la barre d'outils Leaflet, cliquez pour ajouter des points, <strong style={{ color: C.text }}>double-cliquez</strong> pour fermer.</>
                }
              </Typography>
            </Box>

            {/* Leaflet map */}
            <Box ref={mapContainerRef} sx={{
              width: "100%", height: { xs: 280, md: 360 },
              borderRadius: "8px", overflow: "hidden",
              border: `1px solid ${errors.zone ? C.error : C.borderInput}`, background: "var(--c-surface-3)",
            }} />
            {errors.zone && (
              <Typography sx={{ fontSize: "11px", color: C.error, mt: "8px" }}>{errors.zone}</Typography>
            )}

            {/* Zone badge — rectangle */}
            {zoneMode === "rectangle" && form.latMin && (
              <Box sx={{
                mt: 1.5, px: 2, py: 1,
                background: C.successBg, border: `1px solid ${C.successBdr}`,
                borderRadius: "8px", display: "flex", alignItems: "center", gap: 1,
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: C.success,
                  boxShadow: "none", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "12px", color: C.success, fontWeight: 500 }}>
                  Zone définie · {form.latMin}°N {form.lngMin}°E → {form.latMax}°N {form.lngMax}°E
                </Typography>
              </Box>
            )}

            {/* Zone badge + clear — polygone */}
            {zoneMode === "polygon" && zonePolygon.length > 0 && (
              <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{
                  flex: 1, px: 2, py: 1,
                  background: C.successBg, border: `1px solid ${C.successBdr}`,
                  borderRadius: "8px", display: "flex", alignItems: "center", gap: 1,
                }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: C.success,
                    boxShadow: "none", flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "12px", color: C.success, fontWeight: 500 }}>
                    Polygone défini · {zonePolygon.length} points
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={clearZone} sx={{
                  borderColor: "rgba(162,58,13,0.4)", color: C.error,
                  borderRadius: "8px", textTransform: "none", fontSize: "12px",
                  whiteSpace: "nowrap", "&:hover": { background: "rgba(162,58,13,0.08)" },
                }}>
                  Effacer la zone
                </Button>
              </Box>
            )}
          </Card>
        </Grid>

        {/* ══════════════════════════════════════════
            SEC 2 — Champs de collecte (pleine largeur)
            Formulaire de saisie du Spotter dans l'application
        ══════════════════════════════════════════ */}
        <Grid item xs={12}>
          <Card>
            <SectionHeader n={2} aide={AIDE_CREER_QUETE.champs}>Champs de collecte</SectionHeader>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Typography sx={{ fontSize: "12px", color: C.sub }}>
                {fields.length === 0
                  ? "Ces champs constituent le formulaire de saisie du Spotter dans l'application."
                  : `${fields.length} champ${fields.length > 1 ? "s" : ""} — formulaire de saisie terrain`}
              </Typography>
              <Button startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />} onClick={addField} size="small"
                sx={{
                  background: C.accentBg, color: C.accentSoft,
                  border: `1px solid ${C.accentBorder}`,
                  borderRadius: "8px", textTransform: "none",
                  fontSize: "12px", fontWeight: 600, px: 1.5, py: "6px",
                  "&:hover": { background: "rgba(31,30,27,0.17)", borderColor: C.accentSoft },
                }}>
                Ajouter un champ
              </Button>
            </Box>

            {fields.length === 0 ? (
              <>
                <Box sx={{
                  textAlign: "center", py: 5,
                  border: `1px dashed ${errors.champs ? C.error : "rgba(31,30,27,0.07)"}`,
                  borderRadius: "8px", background: errors.champs ? "rgba(162,58,13,0.04)" : "rgba(31,30,27,0.01)",
                }}>
                  <Typography sx={{ fontSize: "13px", color: errors.champs ? C.error : C.muted }}>
                    Aucun champ — cliquez sur « Ajouter un champ » pour commencer
                  </Typography>
                </Box>
                {errors.champs && (
                  <Typography sx={{ fontSize: "11px", color: C.error, mt: "6px" }}>{errors.champs}</Typography>
                )}
              </>
            ) : (
              <Grid container spacing={1.5}>
                {fields.map((f, idx) => (
                  <Grid item xs={12} sm={6} md={4} xl={3} key={f.id}>
                    <Box sx={{
                      background: "var(--c-surface)", borderRadius: "8px",
                      border: `0.5px solid ${C.borderInput}`, overflow: "hidden",
                      transition: "border-color 0.14s",
                      "&:hover": { borderColor: "rgba(31,30,27,0.22)" },
                    }}>
                      {/* strip */}
                      <Box sx={{
                        display: "flex", alignItems: "center", px: 1.5, py: "8px",
                        background: "rgba(31,30,27,0.06)", borderBottom: `0.5px solid ${C.border}`,
                      }}>
                        <Typography sx={{ flex: 1, fontSize: "10px", fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.12em", color: C.accentSoft }}>
                          Champ {idx + 1}
                        </Typography>
                        <Box sx={{ display: "flex" }}>
                          <Tooltip title="Monter"><span>
                            <IconButton size="small" disabled={idx === 0} onClick={() => moveField(idx, -1)}
                              sx={{ color: C.muted, p: "3px", "&:not(:disabled):hover": { color: C.accentSoft } }}>
                              <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span></Tooltip>
                          <Tooltip title="Descendre"><span>
                            <IconButton size="small" disabled={idx === fields.length - 1} onClick={() => moveField(idx, 1)}
                              sx={{ color: C.muted, p: "3px", "&:not(:disabled):hover": { color: C.accentSoft } }}>
                              <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </span></Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton size="small" onClick={() => removeField(f.id)}
                              sx={{ color: C.error, opacity: 0.5, p: "3px", "&:hover": { opacity: 1 } }}>
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      {/* body */}
                      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <Box>
                          <Label>Label</Label>
                          <TextField size="small" fullWidth value={f.label}
                            onChange={e => updateField(f.id, "label", e.target.value)}
                            placeholder="Nom du champ…" sx={F} />
                        </Box>
                        <Box>
                          <Label>Type</Label>
                          <FormControl fullWidth size="small" sx={F}>
                            <Select value={f.type}
                              onChange={e => updateField(f.id, "type", e.target.value)}
                              MenuProps={menuPaper}>
                              {FIELD_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Box>
                        {f.type === "choix" && (
                          <Box>
                            <Label>Options (virgule)</Label>
                            <TextField size="small" fullWidth value={f.optionsJson}
                              placeholder="Oui, Non, NSP"
                              onChange={e => updateField(f.id, "optionsJson", e.target.value)}
                              sx={F} />
                          </Box>
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                          pt: 0.5, borderTop: `0.5px solid ${C.border}` }}>
                          <Typography sx={{ fontSize: "11px", color: C.label, fontWeight: 600,
                            textTransform: "uppercase", letterSpacing: "0.08em" }}>
                            Obligatoire
                          </Typography>
                          <Switch size="small" checked={f.obligatoire}
                            onChange={e => updateField(f.id, "obligatoire", e.target.checked)}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": { color: C.accentSoft },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: C.accent },
                              "& .MuiSwitch-track": { backgroundColor: "rgba(31,30,27,0.12)" },
                            }} />
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Card>
        </Grid>

        {/* ══════════════════════════════════════════
            SEC 4 — Fréquence & durée (col gauche)
        ══════════════════════════════════════════ */}
        <Grid item xs={12} lg={6}>
          <Card>
            <SectionHeader n={4} aide={AIDE_CREER_QUETE.dates}>Fréquence &amp; durée</SectionHeader>
            <Grid container spacing={2}>

              <Grid item xs={12}>
                <Label aide={AIDE_CREER_QUETE.frequence}>Fréquence de collecte</Label>
                <FormControl fullWidth sx={F}>
                  <Select value={form.frequence}
                    onChange={e => set("frequence", e.target.value)}
                    MenuProps={menuPaper}>
                    {FREQUENCES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              {/* Toggle mission récurrente */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: C.input, borderRadius: "8px",
                  border: `1px solid ${missionRecurrente ? C.accentBorder : C.borderInput}`,
                  px: 2, py: 1.5 }}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>
                        Mission récurrente
                      </Typography>
                      <BoutonAide texte={AIDE_CREER_QUETE.missionRecurrente} />
                    </Box>
                    <Typography sx={{ fontSize: "11px", color: C.sub, mt: "2px" }}>
                      {missionRecurrente
                        ? "Des cycles seront générés automatiquement"
                        : "Mission unique, sans cycle récurrent"}
                    </Typography>
                  </Box>
                  <Switch
                    checked={missionRecurrente}
                    onChange={e => setMissionRecurrente(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: C.accentSoft },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: C.accent },
                    }}
                  />
                </Box>
              </Grid>

              {missionRecurrente && (<>
                <Grid item xs={12} sm={6}>
                  <Label>Fréquence des cycles</Label>
                  <FormControl fullWidth sx={F}>
                    <Select value={frequenceCycle}
                      onChange={e => setFrequenceCycle(e.target.value)}
                      MenuProps={menuPaper}>
                      <MenuItem value="hebdomadaire">Hebdomadaire</MenuItem>
                      <MenuItem value="mensuelle">Mensuelle</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Label error={errors.dateFinContrat}>Date de fin du contrat</Label>
                  <TextField type="date" fullWidth value={dateFinContrat}
                    onChange={e => setDateFinContrat(e.target.value)}
                    error={!!errors.dateFinContrat} helperText={errors.dateFinContrat}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      ...F,
                      "& .MuiInputBase-input": { color: dateFinContrat ? C.text : C.muted, padding: "10px 12px" },
                      "& input::-webkit-calendar-picker-indicator": { filter: "invert(0.5)", cursor: "pointer" },
                    }} />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                    borderRadius: "8px", px: 2, py: "10px",
                  }}>
                    <Typography sx={{ fontSize: "12px", color: C.accentSoft, lineHeight: 1.7 }}>
                      ℹ Un nouveau cycle est créé automatiquement à chaque période tant que la quête
                      est publiée et que la fin du contrat n'est pas passée ; la mission est archivée
                      à la fin du contrat. Sans fin de contrat, la date de fin de la quête fait office.
                    </Typography>
                  </Box>
                </Grid>
                {(() => {
                  if (!dateFinContrat || !form.dateDebut || budgetNum <= 0) return null;
                  const cycleMs = frequenceCycle === 'hebdomadaire' ? 7 * 86400000 : 30 * 86400000;
                  const contractMs = new Date(dateFinContrat).getTime() - new Date(form.dateDebut).getTime();
                  if (contractMs <= 0) return null;
                  const nCycles = Math.max(1, Math.round(contractMs / cycleMs));
                  const total = nCycles * budgetNum;
                  return (
                    <Grid item xs={12}>
                      <Box sx={{
                        background: C.successBg, border: `1px solid ${C.successBdr}`,
                        borderRadius: "8px", px: 2, py: "10px",
                      }}>
                        <Typography sx={{ fontSize: "12px", color: C.success, fontWeight: 600 }}>
                          Estimation : {nCycles} cycle{nCycles > 1 ? "s" : ""} × {budgetNum.toLocaleString("fr-FR")} F CFA = {total.toLocaleString("fr-FR")} F CFA
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })()}
              </>)}

              <Grid item xs={6}>
                <Label>Date de début</Label>
                <TextField type="date" fullWidth value={form.dateDebut}
                  onChange={e => set("dateDebut", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    ...F,
                    "& .MuiInputBase-input": { color: form.dateDebut ? C.text : C.muted, padding: "10px 12px" },
                    "& input::-webkit-calendar-picker-indicator": { filter: "invert(0.5)", cursor: "pointer" },
                  }} />
              </Grid>

              <Grid item xs={6}>
                <Label error={errors.dateFin}>Date de fin</Label>
                <TextField type="date" fullWidth value={form.dateFin}
                  onChange={e => set("dateFin", e.target.value)}
                  error={!!errors.dateFin} helperText={errors.dateFin}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    ...F,
                    "& .MuiInputBase-input": { color: form.dateFin ? C.text : C.muted, padding: "10px 12px" },
                    "& input::-webkit-calendar-picker-indicator": { filter: "invert(0.5)", cursor: "pointer" },
                  }} />
              </Grid>

              {/* Durée totale + jours restants */}
              {(duration || joursRestants !== null) && (
                <Grid item xs={12}>
                  <Box sx={{
                    display: "grid", gridTemplateColumns: duration && joursRestants !== null ? "1fr 1fr" : "1fr",
                    gap: 1.5,
                  }}>
                    {duration && (
                      <Box sx={{
                        background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                        borderRadius: "8px", px: 2, py: "10px",
                      }}>
                        <Typography sx={{ fontSize: "11px", color: C.label, textTransform: "uppercase",
                          letterSpacing: "0.09em", fontWeight: 600, mb: "2px" }}>
                          Durée totale
                        </Typography>
                        <Typography sx={{ fontSize: "20px", fontWeight: 800, color: C.accentSoft, lineHeight: 1.2 }}>
                          {duration}
                        </Typography>
                      </Box>
                    )}
                    {joursRestants !== null && (
                      <Box sx={{
                        background: C.successBg, border: `1px solid ${C.successBdr}`,
                        borderRadius: "8px", px: 2, py: "10px",
                      }}>
                        <Typography sx={{ fontSize: "11px", color: C.label, textTransform: "uppercase",
                          letterSpacing: "0.09em", fontWeight: 600, mb: "2px" }}>
                          Jours restants
                        </Typography>
                        <Typography sx={{ fontSize: "20px", fontWeight: 800, color: C.success, lineHeight: 1.2 }}>
                          {joursRestants}j
                        </Typography>
                        <Typography sx={{ fontSize: "10px", color: "rgba(53,106,15,0.55)", mt: "2px" }}>
                          jusqu'à la date de fin
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Collectes attendues — cible de la barre de progression dans l'app */}
              <Grid item xs={12}>
                <Box sx={{
                  background: C.input, borderRadius: "8px",
                  border: `1px solid ${C.borderInput}`, p: "12px 14px",
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography sx={{ fontSize: "11px", color: errors.collectesAttendues ? C.error : C.label, fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: "0.09em" }}>
                      Collectes attendues (cible) *
                    </Typography>
                    <BoutonAide texte={AIDE_CREER_QUETE.collectesAttendues} />
                    {/* Proposé par le comptage de zone, jamais imposé. La
                        densité en dur du navigateur a disparu au lot 5 : ce
                        nombre est ce que Tribe a RÉELLEMENT référencé sur les
                        types ciblés, pas une extrapolation. */}
                    {comptage?.comptage.totalCible != null && comptage.comptage.totalCible > 0 && (
                      <Box
                        component="button" type="button"
                        onClick={() => set("collectesAttendues", String(comptage.comptage.totalCible))}
                        sx={{
                          border: `1px dashed ${C.accentBorder}`, background: "transparent",
                          color: C.accentSoft, borderRadius: "8px", px: "10px", py: "3px",
                          fontSize: "10px", cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        {comptage.comptage.totalCible} lieux de ces types référencés dans la zone — utiliser
                      </Box>
                    )}
                    <Box sx={{
                      fontSize: "10px", color: C.accentSoft,
                      background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                      borderRadius: "8px", px: "6px", py: "1px", fontWeight: 600,
                    }}>
                      Barre de progression dans l'app
                    </Box>
                  </Box>
                  <TextField type="number" fullWidth value={form.collectesAttendues}
                    onChange={e => set("collectesAttendues", e.target.value)}
                    placeholder="Ex : 500"
                    inputProps={{ min: 1 }}
                    error={!!errors.collectesAttendues}
                    helperText={errors.collectesAttendues}
                    sx={F} />
                  <Typography sx={{ fontSize: "11px", color: C.muted, mt: 1, lineHeight: 1.5 }}>
                    Affiché comme «&nbsp;cible&nbsp;» sur la carte de la quête dans l'application.
                    La progression compte les collectes reçues (hors rejetées) et part de&nbsp;0.
                  </Typography>
                </Box>
              </Grid>

            </Grid>
          </Card>
        </Grid>

        {/* ══════════════════════════════════════════
            SEC 5 — Budget & Rémunération (col droite)
        ══════════════════════════════════════════ */}
        <Grid item xs={12} lg={6}>
          <Card>
            <SectionHeader n={5}>Budget &amp; Rémunération</SectionHeader>
            <Grid container spacing={2}>

              {/* Budget client */}
              <Grid item xs={12}>
                <Label error={errors.budgetClient} aide={AIDE_CREER_QUETE.budget}>Budget client (F CFA) *</Label>
                  {refusServeur && (
                    <Typography sx={{ fontSize: "12px", color: C.error, mb: 1, whiteSpace: "pre-wrap" }}>{refusServeur}</Typography>
                  )}
                <TextField
                  type="text"
                  fullWidth
                  value={form.budgetClient}
                  onChange={e => { setMontantsTouches(true); set("budgetClient", e.target.value); }}
                  placeholder="ex: 500 000"
                  error={!!errors.budgetClient}
                  helperText={errors.budgetClient}
                  sx={F}
                />
              </Grid>

              {/* Tips par collecte — ce que gagne le Spotter, en Tips */}
              <Grid item xs={12}>
                <Label error={errors.tokensParCollecte} aide={AIDE_CREER_QUETE.tipsParCollecte}>Tips par collecte *</Label>
                <TextField
                  type="number"
                  fullWidth
                  value={form.tokensParCollecte}
                  onChange={e => { setMontantsTouches(true); set("tokensParCollecte", e.target.value); }}
                  placeholder="ex: 20"
                  inputProps={{ min: 1, step: 1 }}
                  error={!!errors.tokensParCollecte}
                  helperText={errors.tokensParCollecte}
                  sx={F}
                />
                <Typography sx={{ fontSize: "11px", color: C.muted, mt: "4px" }}>
                  {estimation ? `Suggestion du moteur : ${estimation.recompenseSpotterTips} Tips.` : "Aucune suggestion du moteur."}
                  {valeurDuTipCfa && tipsSaisis > 0
                    ? ` Retenu : ≈ ${(tipsSaisis * valeurDuTipCfa).toLocaleString("fr-FR")} F CFA par collecte.`
                    : ""}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1, flexWrap: "wrap" }}>
                  <Button size="small" onClick={lancerEstimation}
                    disabled={!descripteurPricing || estimationEnCours}
                    startIcon={estimationEnCours ? <CircularProgress size={12} sx={{ color: C.accentSoft }} /> : undefined}
                    sx={{
                      background: C.accentBg, color: C.accentSoft, border: `1px solid ${C.accentBorder}`,
                      borderRadius: "8px", textTransform: "none", fontSize: "12px", fontWeight: 600, px: 1.5,
                      "&:hover": { background: "rgba(31,30,27,0.17)" },
                      "&.Mui-disabled": { color: C.muted, borderColor: C.borderInput },
                    }}>
                    Estimer avec le moteur
                  </Button>
                  <Typography sx={{ fontSize: "11px", color: C.muted, lineHeight: 1.5 }}>
                    {!descripteurPricing
                      ? "Cochez au moins un type de lieu ciblé pour estimer."
                      : estimation && cleEstimee !== clePricing
                        ? "Paramètres modifiés depuis l'estimation : relancez-la si besoin."
                        : ""}
                  </Typography>
                </Box>
              </Grid>

              {/* Confirmation explicite du prix (D3) */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox checked={prixConfirme}
                      onChange={e => setPrixConfirme(e.target.checked)}
                      size="small" sx={{ color: C.muted, "&.Mui-checked": { color: C.accentSoft } }} />
                  }
                  label={
                    <Typography sx={{ fontSize: "12px", color: errors.prixConfirme && !prixConfirme ? C.error : C.text }}>
                      Je confirme le prix (Tips par collecte et budget client)
                    </Typography>
                  }
                />
                <BoutonAide texte={AIDE_CREER_QUETE.confirmationPrix} />
                </Box>
                {!prixConfirme && (
                  <Typography sx={{ fontSize: "11px", color: C.muted, lineHeight: 1.5 }}>
                    L'enregistrement reste désactivé tant que le prix n'est pas confirmé.
                    Modifier un montant annule la confirmation.
                  </Typography>
                )}
              </Grid>

              {/* Marge Tribe slider */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                  <Label aide={AIDE_CREER_QUETE.marge}>Marge Tribe</Label>
                  <Box sx={{
                    px: "10px", py: "2px", borderRadius: "8px",
                    background: C.accentBg, border: `1px solid ${C.accentBorder}`,
                  }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.accentSoft }}>
                      {form.margeTribe}%
                    </Typography>
                  </Box>
                </Box>
                <Slider
                  value={form.margeTribe}
                  min={10} max={50} step={1}
                  onChange={(_, v) => set("margeTribe", v as number)}
                  sx={{
                    color: C.accent,
                    "& .MuiSlider-thumb": { width: 16, height: 16, border: `2px solid ${C.accentSoft}` },
                    "& .MuiSlider-rail": { background: "rgba(31,30,27,0.12)" },
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: "10px", color: C.muted }}>10%</Typography>
                  <Typography sx={{ fontSize: "10px", color: C.muted }}>50%</Typography>
                </Box>
              </Grid>

              {/* Bloc calcul automatique */}
              {budgetNum > 0 && (
                <Grid item xs={12}>
                  <Box sx={{
                    background: "rgba(31,30,27,0.06)",
                    border: "1px solid rgba(31,30,27,0.15)",
                    borderRadius: "8px",
                    p: "16px",
                    display: "flex", flexDirection: "column", gap: "10px",
                  }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: C.label,
                      textTransform: "uppercase", letterSpacing: "0.12em", mb: 0.5 }}>
                      Répartition
                    </Typography>

                    {/* CE QUE LE MOTEUR PROPOSAIT, à côté de ce qui est retenu.
                        L'admin décide toujours — règle absolue — mais l'écart
                        doit se voir, et c'est lui qui calibrera le moteur. */}
                    {estimationErreur && (
                      <Typography sx={{ fontSize: "11px", color: C.error, lineHeight: 1.6 }}>
                        Moteur de pricing injoignable — aucune suggestion. Les montants
                        restent à votre main et l'enregistrement fonctionne normalement.
                      </Typography>
                    )}
                    {estimation && (
                      <Box sx={{ pb: "8px", borderBottom: `1px solid ${C.border}` }}>
                        <Typography sx={{ fontSize: "11px", color: C.sub, lineHeight: 1.7 }}>
                          Le moteur propose{" "}
                          <strong style={{ color: C.text }}>
                            {estimation.recompenseSpotterTips} Tips
                          </strong>{" "}
                          par collecte et{" "}
                          <strong style={{ color: C.text }}>
                            {estimation.coutClientFcfa.toLocaleString("fr-FR")} F CFA
                          </strong>{" "}
                          de budget.
                        </Typography>
                        {(base !== estimation.recompenseSpotterTips
                          || budgetNum !== estimation.coutClientFcfa) && (
                          <Typography sx={{ fontSize: "11px", color: C.gold, lineHeight: 1.7 }}>
                            Vous avez retenu {base} Tips et {budgetNum.toLocaleString("fr-FR")} F CFA.
                            L'écart est conservé : c'est lui qui servira à recaler le moteur.
                          </Typography>
                        )}
                        <Tooltip arrow placement="top" title={
                          <Box sx={{ p: 0.5 }}>
                            {estimation.explication.map((l, i) => (
                              <Typography key={i} sx={{ fontSize: "11px", lineHeight: 1.6 }}>· {l}</Typography>
                            ))}
                          </Box>
                        }>
                          <Typography sx={{
                            fontSize: "10px", color: C.accentSoft, mt: "4px",
                            cursor: "help", textDecoration: "underline dotted",
                          }}>
                            détail du calcul · grille {estimation.rulesetVersion}
                          </Typography>
                        </Tooltip>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: "12px", color: C.sub }}>Tribe gagne</Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: C.accentSoft }}>
                        {tribePart.toLocaleString("fr-FR")} F CFA
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: "12px", color: C.sub }}>Budget Spotters</Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: C.success }}>
                        {budgetCollecteurs.toLocaleString("fr-FR")} F CFA
                      </Typography>
                    </Box>
                    <Box sx={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      pt: "8px", borderTop: `1px solid ${C.border}`,
                    }}>
                      <Typography sx={{ fontSize: "12px", color: C.sub }}>
                        Tips par collecte
                        {valeurDuTipCfa && collectesNum > 0 && (
                          <span style={{ color: C.muted }}>
                            {" "}= {(base * valeurDuTipCfa).toLocaleString("fr-FR")} F CFA
                          </span>
                        )}
                      </Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: 700, color: C.text }}>
                        {collectesNum > 0 && valeurDuTipCfa
                          ? `${base} Tips`
                          : valeurDuTipCfa ? "—" : "configuration indisponible"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}


              {/* Bonus vitesse */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Typography sx={{ fontSize: "15px", lineHeight: 1 }}>🏆</Typography>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>
                    Bonus vitesse — premiers arrivés
                  </Typography>
                  <BoutonAide texte={AIDE_CREER_QUETE.bonusVitesse} />
                </Box>
                <Box sx={{ background: C.input, borderRadius: "8px", border: `1px solid ${C.borderInput}`, overflow: "hidden" }}>
                  {form.bonusTiers.map((t, i) => {
                    const bMultiplier = parseFloat(t.multiplier) || 1;
                    const bTips = base > 0 ? Math.round(base * bMultiplier) : null;
                    return (
                      <Box key={i} sx={{ borderBottom: `1px solid ${C.border}`, px: 2, py: 1.25 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Typography sx={{ fontSize: "13px", lineHeight: 1 }}>{i === 0 ? "🥇" : "🥈"}</Typography>
                          <Typography sx={{ fontSize: "12px", fontWeight: 600, color: i === 0 ? C.gold : C.silver }}>
                            {i === 0
                              ? `Top ${t.topN || "N"} premiers`
                              : `Du rang ${parseInt(form.bonusTiers[0]?.topN || "0") + 1} au rang ${t.topN || "N"}`}
                          </Typography>
                          {bTips !== null && (
                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: i === 0 ? C.gold : C.silver, ml: "auto" }}>
                              = {bTips} Tips
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                          <Box>
                            <Label>Top N</Label>
                            <TextField size="small" type="number" fullWidth value={t.topN}
                              onChange={e => setBonusTier(i, "topN", e.target.value)}
                              placeholder="N" inputProps={{ min: 1 }} sx={F} />
                          </Box>
                          <Box>
                            <Label>Multiplicateur ×</Label>
                            <TextField size="small" type="number" fullWidth value={t.multiplier}
                              onChange={e => setBonusTier(i, "multiplier", e.target.value)}
                              placeholder="Ex : 2" inputProps={{ min: 1, step: 0.1 }} sx={F} />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                  <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "13px", lineHeight: 1 }}>👤</Typography>
                      <Typography sx={{ fontSize: "12px", color: C.sub }}>
                        Reste → × 1 {base > 0 ? `= ${base} Tips` : "(automatique)"}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "11px", color: C.muted, fontStyle: "italic" }}>non modifiable</Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Aperçu répartition */}
              {base > 0 && (
                <Grid item xs={12}>
                  <Box sx={{
                    background: C.previewBg, borderRadius: "8px",
                    border: `1px solid ${C.accentBorder}`, overflow: "hidden",
                  }}>
                    <Box sx={{
                      px: 2, py: "9px", background: C.accentBg,
                      borderBottom: `1px solid rgba(31,30,27,0.14)`,
                      display: "flex", alignItems: "center", gap: 1,
                    }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: C.accentSoft, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: C.accentSoft,
                        textTransform: "uppercase", letterSpacing: "0.12em" }}>
                        Aperçu répartition Tips
                      </Typography>
                    </Box>
                    <Box sx={{ px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: "10px" }}>
                      {top1 > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: "12px", color: C.sub }}>
                            Top {top1} premiers
                          </Typography>
                          <Box sx={{ px: "10px", py: "3px", borderRadius: "8px", background: C.goldBg, border: `1px solid ${C.goldBdr}` }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: C.gold }}>
                              {Math.round(base * mult1)} Tips
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {top2 > top1 && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography sx={{ fontSize: "12px", color: C.sub }}>Rang {top1 + 1}–{top2}</Typography>
                          <Box sx={{ px: "10px", py: "3px", borderRadius: "8px", background: C.silverBg, border: `1px solid ${C.silverBdr}` }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: C.silver }}>
                              {Math.round(base * mult2)} Tips
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                        pt: "8px", borderTop: `1px solid ${C.border}` }}>
                        <Typography sx={{ fontSize: "12px", color: C.sub }}>Reste</Typography>
                        <Box sx={{ px: "10px", py: "3px", borderRadius: "8px", background: C.accentBg, border: `1px solid ${C.accentBorder}` }}>
                          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: C.accentSoft }}>
                            {base} Tips
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Card>
        </Grid>

        {/* ══════════════════════════════════════════
            SEC 6 — Validation des collectes (col gauche)
        ══════════════════════════════════════════ */}
        <Grid item xs={12} md={6}>
          <Card>
            <SectionHeader n={6} aide={AIDE_CREER_QUETE.validation}>Validation des collectes</SectionHeader>
            <Grid container spacing={2}>

              {/* Toggle manuel / auto */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: C.input, borderRadius: "8px", border: `1px solid ${C.borderInput}`,
                  px: 2, py: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: C.text }}>
                      {validationAuto ? "Automatique" : "Manuelle"}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: C.sub, mt: "2px" }}>
                      {validationAuto
                        ? "Les collectes sont validées selon les règles définies"
                        : "Chaque collecte doit être validée manuellement"}
                    </Typography>
                  </Box>
                  <Switch
                    checked={validationAuto}
                    onChange={e => setValidationAuto(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: C.success },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: C.success },
                    }}
                  />
                </Box>
              </Grid>

              {/* Vague Vision : analyse photo automatique pour cette quête,
                  même si le réglage global est désactivé. Aide à la décision. */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: C.text }}>
                        Analyse photo automatique
                      </Typography>
                      <BoutonAide texte={AIDE_CREER_QUETE.analysePhoto} />
                    </Box>
                    <Typography sx={{ fontSize: "11px", color: C.sub, mt: "2px" }}>
                      Analyse Vision des photos de cette quête (aide à la décision, aucun paiement)
                    </Typography>
                  </Box>
                  <Switch
                    checked={analysePhotoAuto}
                    onChange={e => setAnalysePhotoAuto(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: C.success },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: C.success },
                    }}
                  />
                </Box>
              </Grid>

              {/* Ce que fait réellement la validation automatique.
                  Les réglages précédents — GPS, champs obligatoires, photo
                  requise, seuil de confiance — étaient des cases sans effet :
                  le serveur applique le géorepérage et le contrôle des champs
                  obligatoires sans les consulter, et ni la photo requise ni
                  le score de confiance n'existent côté serveur. Une case qui
                  ne commande rien laisse croire qu'on configure. */}
              {validationAuto && (
                <Grid item xs={12}>
                  <Box sx={{
                    background: C.input, borderRadius: "8px",
                    border: `1px solid ${C.borderInput}`, px: 1.5, py: 1.25,
                  }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.label,
                      textTransform: "uppercase", letterSpacing: "0.10em", mb: "6px" }}>
                      Contrôles appliqués
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: C.sub, lineHeight: 1.7 }}>
                      • La position du Spotter doit être dans la zone définie<br />
                      • Tous les champs obligatoires doivent être remplis
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: C.muted, mt: "6px", lineHeight: 1.6 }}>
                      Ces deux contrôles sont systématiques et ne se règlent pas.
                      Une soumission qui échoue reste en attente de validation manuelle.
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Card info */}
              <Grid item xs={12}>
                <Box sx={{
                  background: "rgba(53,106,15,0.06)", border: "1px solid rgba(53,106,15,0.20)",
                  borderRadius: "8px", px: 2, py: 1.5,
                }}>
                  <Typography sx={{ fontSize: "11px", color: C.success, lineHeight: 1.6 }}>
                    {validationAuto
                      ? "Les collectes conformes aux règles seront validées automatiquement. Les autres passeront en revue manuelle."
                      : "Toutes les collectes attendent votre validation dans la vue détail de la quête."}
                  </Typography>
                </Box>
              </Grid>

            </Grid>
          </Card>
        </Grid>

        {/* ══════════════════════════════════════════
            SEC 7 — Cohorte & publication (pleine largeur)
        ══════════════════════════════════════════ */}
        <Grid item xs={12}>
          <Card>
            <SectionHeader n={7} aide={AIDE_CREER_QUETE.cohorte}>Cohorte &amp; publication</SectionHeader>
            <Grid container spacing={3}>

              {/* Cohorte — colonne gauche */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.label,
                  textTransform: "uppercase", letterSpacing: "0.10em", mb: 1.5 }}>
                  Critères d'éligibilité
                </Typography>
                <Grid container spacing={1.5}>

                  <Grid item xs={6}>
                    <Label>Niveau minimum</Label>
                    <FormControl fullWidth size="small" sx={F}>
                      <Select value={form.niveauMin}
                        onChange={e => set("niveauMin", e.target.value)} MenuProps={menuPaper}>
                        {niveauxOptions.map(n => <MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <Label>Ancienneté minimum</Label>
                    <FormControl fullWidth size="small" sx={F}>
                      <Select value={form.ancienneteMin}
                        onChange={e => set("ancienneteMin", e.target.value)} MenuProps={menuPaper}>
                        {ANCIENNETES.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <Label>Statut du Spotter</Label>
                    <FormControl fullWidth size="small" sx={F}>
                      <Select value={form.statutCollecteur}
                        onChange={e => set("statutCollecteur", e.target.value)} MenuProps={menuPaper}>
                        {["Tous","Actifs uniquement"].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <Label>Collectes min. déjà faites</Label>
                    <TextField type="number" fullWidth size="small"
                      value={form.collectesMin}
                      onChange={e => set("collectesMin", e.target.value)}
                      placeholder="0" inputProps={{ min: 0 }} sx={F}
                      helperText="Lieux et soumissions validés" />
                  </Grid>
                </Grid>

                <Typography sx={{ fontSize: "11px", color: C.sub, mt: 1.5, lineHeight: 1.6 }}>
                  La quête n'est visible et poussée qu'aux Spotters qui remplissent ces critères
                  et se trouvent près de sa zone (position de l'app, sinon dernière collecte).
                </Typography>

                {/* Le badge « ~142 collecteurs éligibles estimés » était une
                    constante : aucune source serveur ne compte les Spotters
                    éligibles. Retiré plutôt que d'afficher un faux chiffre. */}
              </Grid>

              {/* Divider vertical */}
              <Grid item xs={12} md="auto" sx={{ display: { xs: "none", md: "flex" }, alignItems: "stretch" }}>
                <Box sx={{ width: "1px", background: C.border, mx: 1 }} />
              </Grid>

              {/* Publication — colonne droite */}
              <Grid item xs={12} md>
                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: C.label,
                  textTransform: "uppercase", letterSpacing: "0.10em", mb: 1.5 }}>
                  Publication
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>

                  {/* L'interrupteur « Quête active » n'avait aucun effet : la
                      publication est une action explicite de la fiche. */}
                  <Box sx={{
                    background: C.input, borderRadius: "8px", border: `1px solid ${C.borderInput}`,
                    px: 1.5, py: 1,
                  }}>
                    <Typography sx={{ fontSize: "12px", color: C.sub, lineHeight: 1.6 }}>
                      {editId
                        ? "Mettre à jour ne change pas le statut de la quête."
                        : "La quête est enregistrée « À valider »."}
                      {" "}Elle n'atteint les Spotters qu'après « Valider et publier » depuis sa fiche.
                    </Typography>
                  </Box>

                  <ToggleRow
                    label="Notifier tous les Spotters (push)"
                    aide={AIDE_CREER_QUETE.notification}
                    sub="Push envoyé à tous les Spotters ayant l'application, à la première publication — sans filtre de cohorte"
                    icon={<NotificationsOutlinedIcon sx={{ fontSize: 16 }} />}
                    checked={form.notifPush}
                    onChange={v => set("notifPush", v)}
                  />

                  <ToggleRow
                    label="Afficher en popup dans l'app"
                    sub="Notification in-app à l'ouverture de l'application"
                    icon={<PhoneAndroidOutlinedIcon sx={{ fontSize: 16 }} />}
                    checked={form.notifInApp}
                    onChange={v => set("notifInApp", v)}
                  />

                  {(form.notifPush || form.notifInApp) && (
                    <Box sx={{ mt: 0.5 }}>
                      <Label>Message de notification</Label>
                      <TextField fullWidth multiline rows={3}
                        value={form.notifMessage}
                        onChange={e => { notifEdited.current = true; set("notifMessage", e.target.value); }}
                        placeholder="Contenu du message…"
                        sx={F} />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
}
